/**
 * ArenaController — Stage 5 predicted + reconciled client.
 *
 * Server is authoritative; client predicts its own ship to eliminate the
 * 100ms render-delay input lag that Stage 4 left in. Remote ships still
 * render through the Stage 4 GameState buffer + linear lerp.
 *
 * Flow per Phaser frame:
 *   1. `readKeyboard()` updates `currentInput`.
 *   2. Fixed-timestep accumulator advances `myPredicted` at 60Hz using
 *      `currentInput` and the same physics formula the server runs.
 *   3. `renderInterpolated()` drives remote sprites from the GameState
 *      buffer (skipping our own userId).
 *   4. `renderPredictedSelf()` overlays the predicted state onto our
 *      sprite, overriding any buffer-driven write.
 *
 * Reconciliation: every incoming `GameState` snapshot containing our ship
 * triggers `reconcileSelf()`. It drops acked inputs (seq ≤ server's
 * `lastInputSeq`), rebuilds a baseline = server state + replay of unacked
 * inputs, and either soft-lerps (<50px drift) or hard-snaps (≥50px)
 * `myPredicted` toward that baseline.
 *
 * Coordinate / angle convention: math (0 = +x), shared verbatim with
 * `server/src/physics.ts`. Camera `centerOn(0, 0)` maps world to screen.
 *
 * Stage 5 caveats (deferred):
 *   - Input replay applies one CLIENT_PHYSICS_DT step per buffered input
 *     even though each input is held for ~50ms on the server. Underestimates
 *     server's actual displacement; the soft-lerp absorbs it.
 *   - Server's INPUT_STALE_MS = 150ms zero-input fallback is not mirrored
 *     on the client. Visible mismatch only during long network blips —
 *     reconciliation hard-snaps when threshold trips.
 *   - Pure linear interpolation for remotes; Hermite splines arrive in
 *     Stage 6.
 *   - UI labels use server-provided `guestName` / `shipClass` until
 *     Stage 7 wires Player.profile.
 */

import Phaser from 'phaser';
import type { ListenHandle, PlayServClient, Subscription } from 'playserv-sdk';

import { readGuestIdentity } from '../../identity';
import { statsForTier } from '../../../server/shared/upgrades';
import {
  classById,
  MAX_CLASS_TIER,
  nextChoicesFor,
  STARTER_CLASS_ID,
} from '../../../server/shared/shipClasses';
import { loadMatchConfig, type MatchConfig } from '../../data/matchConfig';
import { loadShopEntries } from '../../data/shop';
import {
  COSMETICS,
  cosmeticById,
  parseCosmeticsField,
  type CosmeticDef,
  type CosmeticsState,
} from '../../../server/shared/cosmetics';
import { ShopModal, type ShopAction } from './ShopModal';
import { QuestTracker } from './QuestTracker';
import { ChatOverlay } from '../../ui/ChatOverlay';
import { ChatFeed } from './ChatFeed';
import { Tooltip } from './Tooltip';
import { TouchControls } from './TouchControls';
import { isTouchDevice } from '../../touch';
import type { QuestMetric } from '../../../server/shared/quests';
import { ascensionStars } from '../../../server/shared/seasons';
import { FireRequest, UpgradeRequest } from '../../../server/shared/events';
import {
  ENERGY_COST_PER_PROJECTILE,
  effectiveStats,
  emptyStatLevels,
  parseStatLevels,
  STAT_ORDER,
  type StatId,
  type StatLevels,
} from '../../../server/shared/upgradeStats';
import { EffectsManager } from './EffectsManager';
import { AsteroidManager } from './AsteroidManager';
import { UpgradePanel } from './UpgradePanel';
import { ClassPickerModal } from './ClassPickerModal';
import { StatPanel } from './StatPanel';
import { CrystalManager } from './CrystalManager';
import { RadarMinimap } from './RadarMinimap';
import { LeaderboardPanel } from './LeaderboardPanel';
import { SoundManager } from './SoundManager';
import { DeathRecapPanel } from './DeathRecapPanel';
import {
  hintsAlreadyShown,
  markHintsShown,
  readPilotName,
} from '../../identity';
import { getPlayer } from '../../data/player';

const GROUP_NAME = 'arena-default';

/**
 * TZ-starblast 1.2 — client input goes to the game server as RPC
 * (`rpc.InvokeRpc` → game_server uplink), NOT group publish (client-side
 * publish is unsupported on the platform, P7a). The service name must match
 * the provisioned game_server function slug. `SetInput` is one-way
 * (FireAndForget) — the platform's designed 20 Hz hot path with per-player
 * latest-wins batching + 10 Hz shed keyed on that exact method name.
 */
const ARENA_SERVICE = 'starblast-arena';

// PSV-1726 mouse-steer — see the aim block in readKeyboard: inside this
// radius around the own ship the cursor stops steering (prevents orbiting
// a stationary cursor under thrust).
const AIM_DEADZONE_PX = 60;
// PSV-1729 QA — mouse-steer only counts as "wanted" while the mouse is
// actually being used. A parked cursor used to re-capture the heading
// the instant A/D was released ("корабль возвращается") — keyboard
// pilots could never hold a turn. Moving the mouse (or pressing a
// button) re-arms steer for this window.
const MOUSE_STEER_ACTIVE_MS = 1_500;
/** PSV-1911 QA — how long a fire-tap keeps steering the hull. */
const TOUCH_FIRE_AIM_MS = 1_200;

/**
 * Typed façade over the `remote(ARENA_SERVICE)` Proxy — the game server's
 * uplink RPC surface (`server/src/arena.ts` uplink.onRpc registrations).
 */
interface ArenaRemote {
  SetInput(input: {
    userId: string;
    seq: number;
    thrust: number;
    turn: number;
    /** PSV-1726 mouse-steer — optional desired heading, radians. */
    aim?: number;
    fire: boolean;
    t: number;
  }): Promise<unknown>;
  Fire(payload: Record<string, unknown>): Promise<unknown>;
  Upgrade(payload: Record<string, unknown>): Promise<unknown>;
  /** PSV-1727 — per-stat purchase; outcome via StatUpgraded/Rejected. */
  UpgradeStat(payload: { statId: string }): Promise<unknown>;
  /** PSV-1890 — pilot rename; outcome via PlayerRenamed broadcast. */
  SetName(payload: { name: string }): Promise<unknown>;
  /** PSV-1890 — death-recap PLAY AGAIN: skip the respawn delay. */
  RespawnNow(payload: Record<string, unknown>): Promise<unknown>;
  /** PSV-1888 — cosmetics commerce (server-authoritative). */
  PurchaseCosmetic(payload: { itemId: string }): Promise<unknown>;
  EquipCosmetic(payload: { itemId: string; kind?: string }): Promise<unknown>;
  /** PSV-1904 — chat relay (server validates + rate-limits + broadcasts). */
  SendChat(payload: { text: string }): Promise<unknown>;
  /** PSV-1905 — class ability (server owns cooldown + effects). */
  UseAbility(payload: Record<string, unknown>): Promise<unknown>;
  /** PSV-1906 — prestige reset for a permanent star. */
  Ascend(payload: Record<string, unknown>): Promise<unknown>;
  /**
   * PSV-1726 — tier-up into a chosen class. The resolved value is a
   * transport-level ack (`{ok, classId?/reason?}`); the authoritative
   * outcome arrives via ShipClassChanged / ShipClassRejected broadcasts.
   */
  ChooseShipClass(payload: { classId: string }): Promise<unknown>;
}

// PSV-1336 — visible ship sprite footprint. Drives sprite scale (=
// SHIP_LENGTH / source PNG width), HP bar y-offset, muzzle nose offset,
// label y-offset. Server-side SHIP_RADIUS = 35 px is the hit detection
// bubble; sprite footprint is larger to leave visual room around it.
const SHIP_LENGTH = 80;
const COLOR_LOCAL = 0x4fff7a;
const COLOR_REMOTE = 0x9aa0b4;

// PSV-1336 — ship sprite texture key, preloaded in `BootScene.preload`.
// Source PNG at `public/assets/ship_b.png` (1024×1024).
const SHIP_TEXTURE_KEY = 'ship_b';
// PSV-1336 — ship_b PNG drawn with nose pointing UP (-Y in Phaser screen
// coords); server angle 0 = nose +X (right, math convention). Offset by
// +π/2 to align the visible nose with the game-world heading.
const SHIP_ART_ANGLE_OFFSET = Math.PI / 2;

// PSV-1336 — projectile tuning. Phase 2 moved authoritative speed/dmg
// to the server (the wire payload carries them); client only needs
// rendering knobs + a local cooldown gate. Phase 3 will move these into
// MatchConfig so admin tweaks propagate without redeploy.
const PROJECTILE_TTL_MS = 1000;
// PSV-1902 QA — ghost pruning: a rendered ship absent from snapshots this
// long is gone (server broadcasts at 20Hz; 2s ≈ 40 missed snapshots).
const GHOST_PRUNE_MS = 2_000;
const PROJECTILE_RADIUS = 4; // Combat balance — see PSV-1336
const COLOR_PROJECTILE = 0xffd84a;

// 10 Hz input heartbeat (PSV-1785). Was 20 Hz — but the deployed platform
// build ignores FireAndForget and processes each connection's InvokeRpc
// SEQUENTIALLY, awaiting the game_server round-trip (~60ms via the uplink)
// before the next frame. At 50ms send interval the per-connection queue
// grew without bound (observed live: inputs arriving 77s stale, ship
// frozen at spawn while seq kept climbing). 100ms leaves headroom over
// the RTT; the input model is держащееся состояние, so 10 Hz loses
// nothing — prediction stays at render rate and the server holds the
// last thrust/turn between heartbeats. TankRoomSample ships ~15 Hz.
const INPUT_INTERVAL_MS = 100;
// Stage 6 v3: 4-snapshot buffer + 75ms delay places renderTime midway
// between the middle pair (buf[1], buf[2]) in steady state, with both
// neighbors (buf[0], buf[3]) available for Catmull-Rom tangents. Cap at
// 4 keeps memory bounded; delay 75ms is mathematically correct for that
// cap (see `renderInterpolated` for the derivation).
const RENDER_DELAY_MS = 75;
const GAMESTATE_BUFFER_MAX = 4;

// Stage 5 — local prediction tuning. Kept module-level for symmetry with
// the rest of this file; values are not instance-specific.
const CLIENT_PHYSICS_DT = 1 / 60; // seconds per predicted-physics step
const INPUT_HISTORY_MAX = 30; // 30 × 50ms = 1.5s of unacked inputs
const RECONCILE_THRESHOLD_PX = 50; // ≥ this → hard snap, < this → soft lerp
const RECONCILE_LERP_ALPHA = 0.2; // per-snapshot blend toward baseline
// Physics constants mirrored from `server/src/physics.ts`. Kept inline to
// avoid a shared package; both sides MUST update together (Stage 5 spec).
const ANGULAR_DECAY_PER_TICK = 0.9;
const ANGULAR_DEADBAND = 0.01;

// ---------- wire types (server → client) ----------

interface GameStateShip {
  userId: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  angularVel: number;
  hp: number;
  isReset?: boolean;
  /** Stage 5+ servers emit; older servers omit (treated as ack-nothing). */
  lastInputSeq?: number;
  /** PSV-1726 — class id, carried for late joiners; older servers omit. */
  shipClass?: string;
  /** PSV-1727 — energy pool (own-ship HUD); older servers omit. */
  energy?: number;
  /** PSV-1906 — prestige stars; older servers omit. */
  asc?: number;
  /** PSV-1888 — equipped cosmetics ('' = default); older servers omit. */
  skin?: string;
  trail?: string;
  death?: string;
  /** PSV-1729 — leaderboard feed (cargo + kills); older servers omit. */
  gems?: number;
  kills?: number;
  /** PSV-1890 — pilot name (chosen or fallback); older servers omit. */
  name?: string;
}

interface GameStatePayload {
  t: number;
  tick: number;
  ships: GameStateShip[];
}

// `PlayerJoined` payload (from server): { userId, guestName, shipClass,
//   spawnX, spawnY, spawnAngle, hp, t }
// `PlayerLeft` payload (from server):   { userId, reason, t }
// These arrive over the wire as `Record<string, unknown>`; the handlers
// narrow each field defensively via stringField/numberField helpers.

// ---------- internal sprite bookkeeping ----------

interface ShipSprite {
  userId: string;
  guestName: string;
  shipClass: string;
  isMine: boolean;
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  // PSV-1888 — applied cosmetic slots (from the snapshot); tracked so the
  // per-snapshot pass only re-tints on change.
  appliedSkin?: string;
  appliedTrail?: string;
  appliedDeath?: string;
}

interface BufferedSnapshot {
  /** Local `Date.now()` at message arrival — used for bracket lookup. */
  receivedAt: number;
  payload: GameStatePayload;
}

interface ShipInputState {
  thrust: -1 | 0 | 1;
  turn: -1 | 0 | 1;
  fire: boolean;
  /** PSV-1726 mouse-steer — desired heading (radians); undefined = keys. */
  aim?: number;
}

/** Self-prediction state. Mirrors the kinematic fields of `ShipState`. */
interface PredictedShipState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  angularVel: number;
  hp: number;
  /**
   * Last server snapshot for self. `null` between initial `PlayerJoined`
   * (when we first construct `myPredicted` from spawn coords) and the
   * first reconciliation tick. Kept around so future reconciliations
   * can detect snapshot regressions if ever needed.
   */
  lastServerSnapshot: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    angle: number;
    angularVel: number;
    tick: number;
    t: number;
  } | null;
}

/**
 * Snapshot of input as it was sent to the server. Reconciliation replays
 * everything with `seq > server.lastInputSeq` against the latest snapshot.
 */
interface BufferedInput {
  seq: number;
  thrust: -1 | 0 | 1;
  turn: -1 | 0 | 1;
  /** PSV-1726 mouse-steer — replayed on reconciliation for determinism. */
  aim?: number;
  fire: boolean;
  /** Client `Date.now()` when this input was sampled / sent. */
  t: number;
}

/**
 * PSV-1336 Phase 1 — live projectile bookkeeping. One entry per active
 * projectile sprite. Motion is integrated locally each frame from `vx`,
 * `vy` (pre-computed at spawn from `angle * speed`); `expiresAt` is the
 * shooter's `fireTime + PROJECTILE_TTL_MS`, evaluated against local
 * `Date.now()` — clock skew across clients can shorten / lengthen visible
 * lifetime by up to a few hundred ms, acceptable for Phase 1.
 */
interface ProjectileEntry {
  sprite: Phaser.GameObjects.Graphics;
  vx: number;
  vy: number;
  // PSV-1902 — position is DERIVED from (origin, velocity, server time),
  // not accumulated per-frame: frame-rate throttling (background tab)
  // and per-screen delta jitter made the same projectile fly at visibly
  // different speeds on different screens.
  originX: number;
  originY: number;
  fireTime: number;
  expiresAt: number;
}

export class ArenaController {
  private readonly client: PlayServClient;
  private readonly scene: Phaser.Scene;
  private readonly myUserId: string;

  private readonly ships = new Map<string, ShipSprite>();
  private readonly gameStateBuffer: BufferedSnapshot[] = [];
  private readonly currentInput: ShipInputState = {
    thrust: 0,
    turn: 0,
    fire: false,
  };

  private subscriptions: Subscription[] = [];
  private inputTimer: Phaser.Time.TimerEvent | null = null;
  /** PSV-1726 — rAF-independent keepalive for hidden tabs (see start()). */
  private backgroundHeartbeat: number | null = null;
  private inputSeq = 0;

  /**
   * RPC proxy to the game server (see ARENA_SERVICE). Created lazily —
   * `remote()` throws NotReadyError before `ready()` resolves, and the
   * controller can be constructed earlier than that.
   */
  private arenaRemoteProxy: ArenaRemote | null = null;

  private get arenaRemote(): ArenaRemote {
    if (this.arenaRemoteProxy === null) {
      this.arenaRemoteProxy = this.client.remote(ARENA_SERVICE, {
        oneWay: ['SetInput'],
      }) as unknown as ArenaRemote;
    }
    return this.arenaRemoteProxy;
  }

  // Stage 5 — local prediction + reconciliation state.
  private matchConfig: MatchConfig | null = null;
  private myPredicted: PredictedShipState | null = null;
  private readonly inputHistory: BufferedInput[] = [];
  private clientPhysicsAccumulator = 0;

  // PSV-1336 Phase 1 — projectile state. Key is `${shooterId}-${fireTime}`
  // (unique per fire across clients). `lastFireAt` is local `Date.now()` of
  // the most recent successful fire — gates `FIRE_COOLDOWN_MS`. `fireKey`
  // is the Space binding; pointerdown is attached via scene event.
  private readonly projectiles = new Map<string, ProjectileEntry>();
  private lastFireAt = 0;
  private fireKey: Phaser.Input.Keyboard.Key | null = null;
  private controlsHintText: Phaser.GameObjects.Text | null = null;

  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private wasd: Record<'W' | 'A' | 'S' | 'D', Phaser.Input.Keyboard.Key> | null =
    null;

  private loadingText: Phaser.GameObjects.Text | null = null;
  private pilotsOnlineText: Phaser.GameObjects.Text | null = null;
  private isJoined = false;
  // PSV-1336 Phase 4 — combat visualization. Driven by ShipDamaged /
  // ShipKilled / ShipRespawned events. `null` until `start()`.
  private effects: EffectsManager | null = null;
  // PSV-1337 — asteroid render manager + HUD state.
  private asteroids: AsteroidManager | null = null;
  /**
   * PSV-1898 — GameState.boot of the server process we trust. A redeploy
   * mints a new one: snapshots from an OLDER process are dropped (two
   * uplinks can coexist for a moment during the swap), and on a NEWER one
   * we clear asteroids/crystals — their ids died with the old process and
   * no AsteroidDestroyed/CrystalsExpired will ever come (observed live as
   * a many-generations-thick ghost field after a redeploy burst).
   */
  private serverBoot: number | null = null;
  /**
   * PSV-1902 — EMA of (server GameState.t − local Date.now()). Everything
   * timed against server timestamps (projectile motion, TTLs) uses
   * serverNow() so machines with skewed clocks render identically.
   */
  private serverClockOffsetMs: number | null = null;
  /** PSV-1902 — latest snapshot ships (magnet eligibility source). */
  private lastSnapshotShips: GameStateShip[] = [];
  /**
   * PSV-1902 QA — Date.now() of the last snapshot that carried each
   * rendered ship. PlayerLeft is a transient event: a client that missed
   * it (reconnect blip) rendered a ghost forever — an invulnerable,
   * motionless hull nobody could interact with. Snapshot is authority:
   * absent > GHOST_PRUNE_MS → prune the render entry.
   */
  private readonly lastSeenInSnapshot = new Map<string, number>();
  /** PSV-1902 — own energy mirror (gates fire feedback client-side). */
  private myEnergy = Number.POSITIVE_INFINITY;
  /** PSV-1888 — own cosmetics (owned + equipped), server-authoritative. */
  private myCosmetics: CosmeticsState = parseCosmeticsField('');
  private shopModal: ShopModal | null = null;
  /** PSV-1889 — daily-quest tracker HUD (render-only; server pushes state). */
  private questTracker: QuestTracker | null = null;
  /** PSV-1912 — shared hover tooltip (one per scene). */
  private tooltip: Tooltip | null = null;
  /** PSV-1911 — virtual joystick; non-null only on touch devices. */
  private touch: TouchControls | null = null;
  private touchChatButton: Phaser.GameObjects.Text | null = null;
  /** PSV-1911 — long-press → tooltip (touch has no hover). */
  private longPressTimer: Phaser.Time.TimerEvent | null = null;
  /** PSV-1911 QA — a fire-tap steers the hull toward the tap for a
   * moment, so shots visibly leave the nose instead of the broadside. */
  private touchFireAim: number | null = null;
  private touchFireAimAt = 0;
  /** PSV-1904 — chat input (DOM, transient) + in-canvas message feed. */
  private chat: ChatOverlay | null = null;
  private chatFeed: ChatFeed | null = null;
  /** PSV-1906 — prestige stars per rendered ship (label surfaces). */
  private readonly ascByUserId = new Map<string, number>();
  /** PSV-1905 — ability HUD line + transient effect visuals. */
  private abilityText: Phaser.GameObjects.Text | null = null;
  private myAbilityCooldownUntil = 0;
  private readonly shieldWalls = new Map<string, number>(); // userId → expiresAt
  private readonly droneFx = new Map<string, number>(); // userId → expiresAt
  private abilityFx: Phaser.GameObjects.Graphics | null = null;
  private shopEntries: readonly CosmeticDef[] = COSMETICS;
  // PSV-1728 — crystal cargo pickups (local ballistics, server endings).
  private crystalManager: CrystalManager | null = null;
  // PSV-1729 — HUD parity: radar, leaderboard, zero-asset SFX.
  private radar: RadarMinimap | null = null;
  private leaderboard: LeaderboardPanel | null = null;
  private readonly sound = new SoundManager();
  /** Leaderboard repaint throttle (repaints at snapshot rate otherwise). */
  private lastLeaderboardAt = 0;
  /** Last wall-clock ms the pointer moved/clicked — arms mouse-steer. */
  private lastPointerActivityAt = 0;
  // PSV-1890 — pilot identity + per-life stats for the death recap.
  private pilotName: string | null = null;
  private deathRecap: DeathRecapPanel | null = null;
  private lifeStartAt = 0;
  private lifeGems = 0;
  private lifeKills = 0;
  // PSV-1886 — current #1 by gems (crown label + gold radar blip).
  private leaderUserId: string | null = null;
  // PSV-1887 — world-event banner + hot-zone glow.
  private activeEvent: { type: string; endsAt: number } | null = null;
  private eventBanner: Phaser.GameObjects.Text | null = null;
  private hotZoneGlow: Phaser.GameObjects.Graphics | null = null;
  /** PSV-1912 — the gold circle explains itself in-world. */
  private hotZoneLabel: Phaser.GameObjects.Text | null = null;
  private gemsText: Phaser.GameObjects.Text | null = null;
  private myGems = 0;
  /**
   * PSV-1785 — the server's speed cap for MY ship (NOT MatchConfig
   * .ShipSpeed), fed into prediction/replay so both sims cap identically.
   * PSV-1726 — now sourced from the ship class tree (`classById`): Player
   * load and the ShipClassChanged broadcast keep it current. The legacy
   * statsForTier sites (Player.listen push, UpgradeApplied) still write
   * it for the old Upgrade flow but are superseded once a
   * ShipClassChanged lands. Buff-driven caps (speedBoost) are not
   * mirrored — a transient, bounded desync.
   */
  private myEngineMax = classById(STARTER_CLASS_ID).engineMax;
  // PSV-1726 — own ship class + per-class rotation speed (rad/s). Agility
  // replaces MatchConfig.ShipRotationSpeed in the prediction physics; the
  // server applies the same per-class value, so both sims turn alike.
  private myShipClassId = STARTER_CLASS_ID;
  private myAgility = classById(STARTER_CLASS_ID).agility;
  private buffsContainer: Phaser.GameObjects.Container | null = null;
  /**
   * Per-active-buff HUD entry. Updated each frame so the countdown text
   * stays current; pruned when the matching `BuffExpired` event fires or
   * when the expiry timestamp passes (defensive — event loss tolerance).
   */
  private readonly activeBuffs = new Map<
    string,
    { expiresAt: number; text: Phaser.GameObjects.Text }
  >();
  // PSV-1336 Phase 4 — cached HpMax per userId so HP bars can render the
  // correct ratio when GameState carries only `hp`. Server's ShipDamaged
  // event delivers hpMax explicitly; we cache it here and reuse on every
  // bar refresh.
  private readonly hpMaxByUserId = new Map<string, number>();
  // PSV-1338 — upgrade HUD + live Player subscription. Panel renders the
  // current tier + next cost + Upgrade button; the listen handle pushes
  // changes (gems consumed, tier advanced) reactively so the panel never
  // shows stale state even with multi-tab edits.
  private upgradePanel: UpgradePanel | null = null;
  // PSV-1726 — class picker modal, opened from the UpgradePanel button.
  private classPicker: ClassPickerModal | null = null;
  // PSV-1727 — per-stat upgrade HUD (hotkeys 1-8 + energy bar) and my
  // authoritative-mirrored stat levels. Levels feed prediction via
  // effectiveStats (speed/agility multiply the class base), so both
  // sims cap identically after every purchase.
  private statPanel: StatPanel | null = null;
  private myStatLevels: StatLevels = emptyStatLevels();
  private playerListenHandle: ListenHandle<Record<string, unknown>> | null =
    null;
  // PSV-1336 Phase 4 — defaults match schema defaults (Player.Ship.HpMax
  // = 100). Used until the first ShipDamaged event tells us otherwise.
  private static readonly DEFAULT_HP_MAX = 100;
  /**
   * Safety net for Bug 2: hide the loading overlay even if `PlayerJoined`
   * never arrives (e.g. it got dropped during a transport reconnect
   * window). Cleared on any successful hide; armed alongside `showLoading`
   * in `start()`.
   */
  private loadingFallbackTimer: Phaser.Time.TimerEvent | null = null;
  private static readonly LOADING_FALLBACK_MS = 5_000;

  /**
   * PSV-1890 — chosen pilot name from the entry screen. Must be called
   * BEFORE start(); the server gets it via SetName once the ship spawns
   * and echoes PlayerRenamed to everyone.
   */
  setPilotName(name: string): void {
    this.pilotName = name;
  }

  constructor(client: PlayServClient, scene: Phaser.Scene) {
    this.client = client;
    this.scene = scene;
    this.myUserId = readGuestIdentity().userId;
  }

  async start(): Promise<void> {
    // Defensive: `myUserId` must be set before any handler can match against
    // it. `readGuestIdentity()` is sync today, but assert anyway so we fail
    // loudly if that ever changes.
    if (typeof this.myUserId !== 'string' || this.myUserId.length === 0) {
      throw new Error('ArenaController: myUserId not initialized');
    }

    // World (0, 0) at screen centre — matches server spawn ring math.
    this.scene.cameras.main.centerOn(0, 0);

    // PSV-1336 — space background. Single Image (not TileSprite) because
    // the camera is fixed (no scroll), so parallax/tile-reveal is moot.
    // Stretched to the full game viewport (1280×720) and placed at depth
    // -100 so every gameplay sprite, HP bar, particle, kill feed entry,
    // and overlay (all at depth ≥ 0) layers cleanly on top.
    const cam = this.scene.cameras.main;
    const bg = this.scene.add.image(0, 0, 'space_bg');
    bg.setOrigin(0.5, 0.5);
    bg.setDisplaySize(cam.width, cam.height);
    bg.setDepth(-100);

    if (this.scene.input.keyboard !== null) {
      this.cursors = this.scene.input.keyboard.createCursorKeys();
      this.wasd = {
        W: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
        A: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        S: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
        D: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      };
      // PSV-1336 Phase 1 — Space binds to fire. `pointerdown` (any mouse
      // button anywhere on the canvas) also fires; both call into the same
      // cooldown-gated handler. Listening to 'down' (not polled in
      // readKeyboard) so a held key doesn't auto-repeat past the cooldown
      // — each fresh keypress triggers one fire attempt.
      this.fireKey = this.scene.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE,
      );

      // PSV-1727 — stat-upgrade hotkeys. Digits 1-8 map to STAT_ORDER
      // (the same order the panel renders). keydown-N events fire on the
      // top-row digits; purchases the server can't honor just bounce
      // back as StatUpgradeRejected, so no client-side affordability
      // gate is needed here.
      const digitKeys = [
        'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT',
      ] as const;
      digitKeys.forEach((keyName, i) => {
        this.scene.input.keyboard!.on(`keydown-${keyName}`, () => {
          this.requestStatUpgrade(STAT_ORDER[i]!);
        });
      });
      // PSV-1729 — M toggles the SFX package.
      this.scene.input.keyboard.on('keydown-M', () => {
        const muted = this.sound.toggleMuted();
        this.upgradePanel?.showToast(muted ? 'Sound: off' : 'Sound: on', 'ok');
      });
      // PSV-1888 — B toggles the cosmetics shop.
      this.scene.input.keyboard.on('keydown-B', () => {
        this.toggleShop();
      });
      // PSV-1889 — Q toggles the quest tracker.
      this.scene.input.keyboard.on('keydown-Q', () => {
        this.questTracker?.toggle();
      });
      // PSV-1904 — Enter opens the chat input (the overlay's capture
      // handler keeps game keys from leaking while it's open).
      this.scene.input.keyboard.on('keydown-ENTER', () => {
        this.chat?.openInput();
      });
      // PSV-1905 — Shift fires the class ability (server validates).
      this.scene.input.keyboard.on('keydown-SHIFT', () => {
        this.requestAbility();
      });
      // PSV-1912 — one scene-level pointermove drives every hover
      // tooltip surface (screen coords; each surface does its own hit
      // test). Modals paint above tooltips' anchors, so hovering is
      // suppressed while the shop is open.
      this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
        this.handleHudHover({ x: pointer.x, y: pointer.y });
      });
      this.scene.input.keyboard.on('keydown-ESC', () => {
        this.shopModal?.hide();
      });
      this.fireKey.on('down', this.handleFireInput, this);
    }
    this.scene.input.on('pointerdown', this.handleFireInput, this);
    // PSV-1729 QA — arm mouse-steer only on real mouse activity.
    this.scene.input.on('pointermove', () => {
      this.lastPointerActivityAt = Date.now();
    });

    this.createPilotsOnlineText();
    this.createControlsHintText();
    this.showLoading('Joining arena…');
    this.armLoadingFallback();

    // Ghost-ships fix: on every transport drop the SDK transitions the
    // session to `recovering`. By the time it reaches `active` again the
    // server may have evicted us (TTL) and respawned at a new position
    // with the same userId, while our local map still holds the stale
    // sprite — producing a visible duplicate. Wipe local state on entry
    // to `recovering`; fresh GameState/PlayerJoined frames repopulate
    // once we're back. `wait` does not fire for the current state, so
    // there's no spurious clear at start time.
    this.subscriptions.push(
      this.client.session.wait('Recovering', () => this.handleSessionRecovering()),
    );
    // PSV-1338 Bug 2 — symmetric subscription on the way back UP. Server
    // may have TTL-evicted us during the recovery window; the existing
    // safety net in `handleGameState` recreates our sprite from the next
    // GameState that lists us. The Active handler re-arms the loading
    // fallback against the post-reconnect cadence and gives us a single
    // place to log the transition for diagnostics.
    this.subscriptions.push(
      this.client.session.wait('Active', () => this.handleSessionActive()),
    );

    await this.client.subscribeToGroup(GROUP_NAME);

    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'GameState',
        (payload) => this.handleGameState(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'PlayerJoined',
        (payload) => this.handlePlayerJoined(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'PlayerLeft',
        (payload) => this.handlePlayerLeft(payload),
      ),
    );
    // PSV-1336 Phase 1 — every client publishes `ProjectileFired` on its
    // own fire (no server validation yet). All clients including the
    // shooter listen here; the shooter's own publish gets dedup'd by
    // `${shooterId}-${fireTime}` since the local prediction path spawned
    // the sprite synchronously.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ProjectileFired',
        (payload) => this.handleProjectileFired(payload),
      ),
    );
    // PSV-1726 projectile-lifecycle sync — despawn the projectile visual
    // at the resolved impact instead of flying through the target.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ProjectileHit',
        (payload) => this.handleProjectileHit(payload),
      ),
    );
    // PSV-1336 Phase 4 — combat visualization events. Server publishes
    // these on every projectile→ship hit (ShipDamaged), on kill confirm
    // (ShipKilled), and on respawn (ShipRespawned). EffectsManager owns
    // the actual sprite/particle work; controller does payload narrowing
    // + name lookup for the kill feed.
    this.effects = new EffectsManager(this.scene);
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShipDamaged',
        (payload) => this.handleShipDamaged(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShipKilled',
        (payload) => this.handleShipKilled(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShipRespawned',
        (payload) => this.handleShipRespawned(payload),
      ),
    );

    // PSV-1337 — asteroid manager + bonus/buff HUD. AsteroidManager owns
    // sprite/HP-bar/glow lifecycle; controller marshals events from the
    // wire and updates the gem counter + active-buff HUD.
    this.asteroids = new AsteroidManager(this.scene);
    // PSV-1728 — crystals fly toward their collector; the lookup feeds
    // the collect tween the ship's current position.
    this.crystalManager = new CrystalManager(
      this.scene,
      (userId) => {
        const entry = this.ships.get(userId);
        return entry !== undefined
          ? { x: entry.sprite.x, y: entry.sprite.y }
          : null;
      },
      // PSV-1902 — magnet targets for the local crystal sim (mirrors the
      // server's eligibility: alive + hold space). Own ship uses the
      // predicted position; remotes use their rendered sprites with
      // gems/hp from the latest snapshot.
      () => this.crystalMagnetTargets(),
    );
    // PSV-1888 — cosmetics: seed own state from the boot-time Player row,
    // build the shop modal, and fetch the storefront-driven item list in
    // the background (shared-table fallback keeps the shop usable).
    try {
      this.myCosmetics = parseCosmeticsField(getPlayer().Cosmetics);
    } catch {
      // Player not loaded — server restore heals equipped slots via the
      // snapshot; owned refills on the first shop interaction.
    }
    this.shopModal = new ShopModal(this.scene, (action) => {
      this.handleShopAction(action);
    });
    this.tooltip = new Tooltip(this.scene);
    this.questTracker = new QuestTracker(this.scene, this.tooltip);
    if (isTouchDevice()) {
      // PSV-1911 — second touch point: joystick + fire tap concurrently.
      this.scene.input.addPointer(1);
      this.touch = new TouchControls(this.scene);
      this.createTouchChatButton();
      this.armLongPressTooltips();
    }
    this.chatFeed = new ChatFeed(this.scene);
    this.chat = new ChatOverlay((text) => {
      void this.arenaRemote
        .SendChat({ text })
        .then((res) => {
          const r = res as { ok?: boolean; reason?: string };
          if (r.ok !== true && r.reason === 'rate limited') {
            this.upgradePanel?.showToast('Slow down :)', 'error');
          }
        })
        .catch(() => {});
    });
    void loadShopEntries().then((entries) => {
      this.shopEntries = entries;
    });
    // PSV-1729 — radar reads the maps the controller already maintains;
    // no extra subscriptions (per ticket).
    this.radar = new RadarMinimap(this.scene, () => ({
      arenaRadius: this.matchConfig?.ArenaRadius ?? 800,
      me:
        this.myPredicted !== null
          ? {
              x: this.myPredicted.x,
              y: this.myPredicted.y,
              angle: this.myPredicted.angle,
            }
          : null,
      ships: Array.from(this.ships.values())
        .filter((sh) => sh.userId !== this.myUserId)
        .map((sh) => ({
          x: sh.sprite.x,
          y: sh.sprite.y,
          leader: sh.userId === this.leaderUserId,
        })),
      asteroids: this.asteroids?.radarBlips() ?? [],
      hotZoneRadius: this.matchConfig?.HotZoneRadius ?? 0,
      eventActive: this.activeEvent !== null,
    }));
    this.leaderboard = new LeaderboardPanel(this.scene, this.myUserId);
    // Web Audio unlock rides the first user gesture (autoplay policy).
    this.scene.input.once('pointerdown', () => this.sound.resume());
    // PSV-1890 — death recap overlay + pilot-name announcement.
    this.deathRecap = new DeathRecapPanel(this.scene, {
      onPlayAgain: () => this.requestRespawnNow(),
      onChangeName: () => this.promptRename(),
    });
    // PSV-1890 QA — do NOT announce here: the ship doesn't exist on the
    // server until the first ShipInput lands, so an immediate SetName
    // bounces off 'ship not active' and the fallback name wins ("имя
    // мелькнуло и слетело"). The announce rides own PlayerJoined below.
    const startingName = this.pilotName ?? readPilotName();
    if (startingName !== null && startingName !== '') {
      this.pilotName = startingName;
    }
    this.maybeShowFirstSessionHints();
    this.createGemsText();
    this.createBuffsHud();
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AsteroidSpawned',
        (payload) => this.handleAsteroidSpawned(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AsteroidUpdated',
        (payload) => this.handleAsteroidUpdated(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AsteroidMoved',
        (payload) => this.handleAsteroidMoved(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AsteroidDestroyed',
        (payload) => this.handleAsteroidDestroyed(payload),
      ),
    );
    // PSV-1728 — crystal lifecycle broadcasts (burst → local ballistics;
    // collected/expired are the authoritative endings).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'CrystalBurst',
        (payload) => this.handleCrystalBurst(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'CrystalCollected',
        (payload) => this.handleCrystalCollected(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'CrystalsExpired',
        (payload) => this.handleCrystalsExpired(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'BonusApplied',
        (payload) => this.handleBonusApplied(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'BuffExpired',
        (payload) => this.handleBuffExpired(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShieldBlocked',
        (payload) => this.handleShieldBlocked(payload),
      ),
    );

    // PSV-1338 — upgrade HUD + group subscriptions. UpgradePanel reads
    // gems / tier reactively (driven by `setupPlayerListen` below).
    // PSV-1726 — the button now opens the class picker; the modal's pick
    // goes out as the ChooseShipClass RPC and the outcome comes back via
    // ShipClassChanged / ShipClassRejected broadcasts. The own gems +
    // class baseline is seeded from `getPlayer()` (loaded by BootScene
    // via loadOrCreatePlayer) before the listen handle pushes updates.
    this.upgradePanel = new UpgradePanel(this.scene, () =>
      this.openClassPicker(),
    );
    this.classPicker = new ClassPickerModal(this.scene, (classId) =>
      this.requestClassChange(classId),
    );
    // PSV-1727 — stat panel (bottom-left). Rows click through to the
    // same RPC the hotkeys use; server broadcasts drive actual state.
    this.statPanel = new StatPanel(this.scene, (statId) =>
      this.requestStatUpgrade(statId),
    );
    try {
      const cached = getPlayer();
      // PSV-1726 — class-derived stats; `classById` tolerates legacy
      // records (pre-tree 'Scout') by falling back to the starter.
      const cachedClass = classById(cached.Ship.Class);
      this.myShipClassId = cachedClass.id;
      this.upgradePanel.setClassId(cachedClass.id);
      this.upgradePanel.setGems(cached.Gems);
      // PSV-1727 — persisted stat levels scale the class base for
      // prediction; refreshMyEffectiveStats also seeds the stat panel.
      this.myStatLevels = parseStatLevels(cached.statLevels);
      this.statPanel.setGems(cached.Gems);
      this.refreshMyEffectiveStats();
    } catch {
      // Player not loaded — Stage 7 init order should preclude this, but
      // panel still renders tier 1 / 0 gems and `setupPlayerListen` will
      // refresh once the first update lands.
    }
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'UpgradeApplied',
        (payload) => this.handleUpgradeApplied(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'UpgradeRejected',
        (payload) => this.handleUpgradeRejected(payload),
      ),
    );
    // PSV-1726 — class-tree broadcasts. ShipClassChanged carries the
    // authoritative post-change stats for ANY player (visual retint /
    // rescale for everyone, panel + prediction updates for own userId);
    // ShipClassRejected only toasts for our own denied attempts.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShipClassChanged',
        (payload) => this.handleShipClassChanged(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ShipClassRejected',
        (payload) => this.handleShipClassRejected(payload),
      ),
    );
    // PSV-1889 — quest engine pushes: own progress renders the tracker,
    // unlocks/dailies toast + sting.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'QuestProgress',
        (payload) => this.handleQuestProgress(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AchievementUnlocked',
        (payload) => this.handleAchievementUnlocked(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'DailyCompleted',
        (payload) => this.handleDailyCompleted(payload),
      ),
    );
    // PSV-1906 — prestige announcements (arena-wide feed + own toast).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'Ascended',
        (payload) => this.handleAscended(payload),
      ),
    );
    // PSV-1905 — ability effects (visuals for every client).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'AbilityUsed',
        (payload) => this.handleAbilityUsed(payload),
      ),
    );
    // PSV-1904 — chat messages (server-relayed).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'ChatMessage',
        (payload) => this.handleChatMessage(payload),
      ),
    );
    // PSV-1727 — per-stat upgrade outcomes. StatUpgraded is broadcast for
    // ANY player (we only consume our own for HUD/prediction; hpMax rides
    // along for the HP-bar cache); StatUpgradeRejected toasts own denials.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'StatUpgraded',
        (payload) => this.handleStatUpgraded(payload),
      ),
    );
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'StatUpgradeRejected',
        (payload) => this.handleStatUpgradeRejected(payload),
      ),
    );
    // PSV-1890 — pilot renames refresh every name surface.
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'PlayerRenamed',
        (payload) => this.handlePlayerRenamed(payload),
      ),
    );
    // PSV-1886 — arena-wide streak announcements (feed + stinger).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'StreakAnnounce',
        (payload) => this.handleStreakAnnounce(payload),
      ),
    );
    // PSV-1887 — world events (banner + zone glow + stinger).
    this.subscriptions.push(
      this.client.waitInGroup<Record<string, unknown>>(
        GROUP_NAME,
        'WorldEvent',
        (payload) => this.handleWorldEvent(payload),
      ),
    );
    this.setupPlayerListen();

    // Stage 5: load MatchConfig so local physics prediction integrates with
    // the same constants the server uses. `loadMatchConfig` itself handles
    // DataFlow throws / missing record / partial fields by falling back to
    // compiled-in defaults — it never throws. Stage 7 will swap this for
    // a DataSubscription so admin-panel edits take effect without reload.
    this.matchConfig = await loadMatchConfig(this.client);
    // PSV-1887 — soft gold glow marking the rich centre belt (world
    // space; pulses via alpha while an event is running — see update()).
    this.hotZoneGlow = this.scene.add.graphics().setDepth(-50);
    this.hotZoneLabel = this.scene.add
      .text(0, 0, 'HOT ZONE \u00d72.5 \u25c6', {
        fontSize: '13px',
        color: '#ffd84a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1)
      .setAlpha(0.55)
      .setDepth(-49);
    this.redrawHotZoneGlow();

    // Heartbeat input loop starts immediately — server uses incoming
    // `ShipInput` as the spawn trigger. With thrust=turn=0 the ship just
    // sits, but server still spawns it and publishes `PlayerJoined` back.
    // PSV-1726 — background-tab survival: Phaser timers ride rAF, which
    // Chrome suspends for hidden/occluded windows — heartbeats stop and
    // the server TTL-evicts the ship in 5s ("second window's ship
    // vanished"). A plain setInterval is only throttled (≥1Hz in
    // background), comfortably inside the TTL, so the idle ship survives
    // until the tab wakes.
    this.backgroundHeartbeat = window.setInterval(() => {
      if (document.visibilityState === 'hidden' || !this.scene.game.hasFocus) {
        this.sendShipInput();
      }
    }, 1_000);
    this.inputTimer = this.scene.time.addEvent({
      delay: INPUT_INTERVAL_MS,
      loop: true,
      callback: () => this.sendShipInput(),
    });
  }

  update(_time: number, delta: number): void {
    // ArenaScene calls this every Phaser frame (~60Hz). We use local
    // wall clock for interpolation, not Phaser's loop time, so it lines
    // up with `receivedAt` in the buffer.
    this.readKeyboard();

    // Stage 5: fixed-timestep accumulator drives self-prediction at 60Hz
    // independent of Phaser's variable frame rate. Phaser's `delta` is ms;
    // CLIENT_PHYSICS_DT is seconds.
    this.clientPhysicsAccumulator += delta / 1000;
    while (this.clientPhysicsAccumulator >= CLIENT_PHYSICS_DT) {
      this.physicsTickPredicted();
      this.clientPhysicsAccumulator -= CLIENT_PHYSICS_DT;
    }

    const now = Date.now();
    this.renderInterpolated(now); // remotes only — own ship is skipped inside
    this.renderPredictedSelf(); // overlays prediction onto own sprite
    // PSV-1336 Phase 1 — integrate projectile motion + TTL cleanup. Uses
    // Phaser's `delta` directly (ms → sec) rather than the prediction
    // accumulator; projectiles are visual-only in Phase 1 so frame-perfect
    // determinism vs server isn't required.
    this.updateProjectiles(delta, now);
    this.updatePilotsOnlineText();
    // PSV-1336 Phase 4 — drive particles, HP bars, hit flashes, kill
    // feed, "you died" countdown. Must run after sprite positions
    // have settled so HP bars and flashes track the latest world coords.
    this.effects?.update(delta);
    // PSV-1337 — drive asteroid position lerps + countdown buff text.
    this.asteroids?.update(delta);
    // PSV-1728 — crystal ballistics + TTL fades.
    this.crystalManager?.update(delta);
    // PSV-1902 UX — the shop modal covered the controls bar / chat stack
    // half-transparently; hide both while it's open.
    const shopOpen = this.shopModal?.isOpen === true;
    this.controlsHintText?.setVisible(!shopOpen);
    this.chatFeed?.setVisible(!shopOpen);
    this.chatFeed?.update();
    this.renderAbilityFx(Date.now()); // PSV-1905
    this.layoutTopLeftHud(); // PSV-1902 UX — dynamic column stacking
    // PSV-1729 — radar redraw (self-throttled to 10Hz).
    this.radar?.update(delta);
    // PSV-1887 — banner countdown + zone pulse.
    this.updateEventBanner();
    this.updateBuffsHud();
    // PSV-1338 — advance upgrade-toast lifetimes.
    this.upgradePanel?.update(delta);
  }

  /**
   * PSV-1726 — dev/test-only state snapshot (Playwright acceptance reads
   * it via the window.__arenaDebug hook). Cheap, no side effects.
   */
  debugSnapshot(): {
    myUserId: string;
    predicted: { x: number; y: number; angle: number } | null;
    input: { thrust: number; turn: number; aim?: number };
    ships: Array<{ userId: string; x: number; y: number }>;
  } {
    return {
      myUserId: this.myUserId,
      predicted:
        this.myPredicted === null
          ? null
          : { x: this.myPredicted.x, y: this.myPredicted.y, angle: this.myPredicted.angle },
      input: {
        thrust: this.currentInput.thrust,
        turn: this.currentInput.turn,
        aim: this.currentInput.aim,
      },
      ships: Array.from(this.ships.values(), (s) => ({
        userId: s.userId,
        x: s.sprite.x,
        y: s.sprite.y,
      })),
    };
  }

  async stop(): Promise<void> {
    if (this.backgroundHeartbeat !== null) {
      window.clearInterval(this.backgroundHeartbeat);
      this.backgroundHeartbeat = null;
    }
    if (this.inputTimer !== null) {
      this.inputTimer.remove(false);
      this.inputTimer = null;
    }
    for (const sub of this.subscriptions) sub.stop();
    this.subscriptions = [];

    for (const ship of this.ships.values()) {
      ship.sprite.destroy();
      ship.label.destroy();
    }
    this.ships.clear();

    // PSV-1336 Phase 1 — release projectile sprites + detach fire input.
    for (const proj of this.projectiles.values()) {
      proj.sprite.destroy();
    }
    this.projectiles.clear();
    if (this.fireKey !== null) {
      this.fireKey.off('down', this.handleFireInput, this);
      this.fireKey = null;
    }
    this.scene.input.off('pointerdown', this.handleFireInput, this);

    this.hideLoading(); // also clears loadingFallbackTimer
    this.pilotsOnlineText?.destroy();
    this.pilotsOnlineText = null;
    this.controlsHintText?.destroy();
    this.controlsHintText = null;
    // PSV-1336 Phase 4 — release all particle pool sprites + HP bars +
    // overlays. Safe to call before any effects events fire since
    // EffectsManager init was synchronous in start().
    this.effects?.destroy();
    this.effects = null;
    this.hpMaxByUserId.clear();
    // PSV-1337 — tear down asteroid sprites + HUD overlays.
    this.asteroids?.destroy();
    this.crystalManager?.destroy();
    this.crystalManager = null;
    this.radar?.destroy();
    this.radar = null;
    this.leaderboard?.destroy();
    this.leaderboard = null;
    this.deathRecap?.destroy();
    this.deathRecap = null;
    this.questTracker?.destroy();
    this.questTracker = null;
    this.tooltip?.destroy();
    this.tooltip = null;
    this.touch?.destroy();
    this.touch = null;
    this.touchChatButton?.destroy();
    this.touchChatButton = null;
    this.longPressTimer?.remove();
    this.longPressTimer = null;
    this.chat?.destroy();
    this.chat = null;
    this.chatFeed?.destroy();
    this.chatFeed = null;
    this.abilityText?.destroy();
    this.abilityText = null;
    this.abilityFx?.destroy();
    this.abilityFx = null;
    this.shopModal?.destroy();
    this.shopModal = null;
    this.eventBanner?.destroy();
    this.eventBanner = null;
    this.hotZoneGlow?.destroy();
    this.hotZoneGlow = null;
    this.hotZoneLabel?.destroy();
    this.hotZoneLabel = null;
    this.sound.destroy();
    this.asteroids = null;
    this.gemsText?.destroy();
    this.gemsText = null;
    for (const entry of this.activeBuffs.values()) entry.text.destroy();
    this.activeBuffs.clear();
    this.buffsContainer?.destroy();
    this.buffsContainer = null;
    // PSV-1338 — close the Player.listen subscription + destroy panel.
    if (this.playerListenHandle !== null) {
      const handle = this.playerListenHandle;
      this.playerListenHandle = null;
      handle.stop().catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[upgrade] Player.listen stop failed:', err);
      });
    }
    this.upgradePanel?.destroy();
    this.upgradePanel = null;
    // PSV-1726 — tear down the class picker (closes it if open).
    this.classPicker?.destroy();
    this.statPanel?.destroy();
    this.statPanel = null;
    this.classPicker = null;

    try {
      await this.client.unsubscribeFromGroup(GROUP_NAME);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[arena] unsubscribeFromGroup failed:', err);
    }

    // PSV-1898 — no client-side Player persist on shutdown any more: the
    // server owns every field and writes via its uplink data channel.
  }

  // ---------- outbound ----------

  private sendShipInput(): void {
    this.inputSeq += 1;
    const input: BufferedInput = {
      seq: this.inputSeq,
      thrust: this.currentInput.thrust,
      turn: this.currentInput.turn,
      aim: this.currentInput.aim,
      fire: this.currentInput.fire,
      t: Date.now(),
    };

    // TZ-starblast 1.2: one-way RPC (FireAndForget) replaces the removed
    // client group publish. The server derives the caller from the
    // platform-validated `player_id` — the payload's userId is legacy
    // convenience only. A rejection (transport mid-reconnect) just skips
    // this tick; the 20 Hz loop resumes next tick.
    void this.arenaRemote
      .SetInput({
        userId: this.myUserId,
        seq: input.seq,
        thrust: input.thrust,
        turn: input.turn,
        aim: input.aim,
        fire: input.fire,
        t: input.t,
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.debug(
          '[arena] SetInput skipped (transport unavailable):',
          err instanceof Error ? err.message : err,
        );
        // Fall through — we still push to history because `myPredicted`
        // advanced with this input regardless of whether server got it.
        // Replay correctness depends on history matching what prediction
        // actually applied, not what server observed.
      });

    this.inputHistory.push(input);
    while (this.inputHistory.length > INPUT_HISTORY_MAX) {
      this.inputHistory.shift();
    }
  }

  private readKeyboard(): void {
    const cur = this.cursors;
    const wasd = this.wasd;
    if (cur === null || wasd === null) return;

    let thrust: -1 | 0 | 1 = 0;
    let turn: -1 | 0 | 1 = 0;

    if (cur.up?.isDown === true || wasd.W.isDown) thrust = 1;
    else if (cur.down?.isDown === true || wasd.S.isDown) thrust = -1;

    if (cur.left?.isDown === true || wasd.A.isDown) turn = -1;
    else if (cur.right?.isDown === true || wasd.D.isDown) turn = 1;

    this.currentInput.thrust = thrust;
    this.currentInput.turn = turn;
    // PSV-1911 — an active joystick owns thrust+aim outright; taps on the
    // fire half must not arm mouse-steer (a shot toward a tap is aimed by
    // the FireRequest itself, the hull shouldn't chase every tap).
    if (this.touch !== null) {
      this.currentInput.thrust = this.touch.thrust();
      // Joystick owns heading while dragged; otherwise a recent fire-tap
      // swings the hull toward the shot (PO device-test: without this the
      // hull never turns and projectiles read as spawning from the side).
      const fireAimFresh =
        this.touchFireAim !== null &&
        Date.now() - this.touchFireAimAt < TOUCH_FIRE_AIM_MS;
      this.currentInput.aim =
        this.touch.aim() ?? (fireAimFresh ? this.touchFireAim! : undefined);
      this.currentInput.fire = false;
      return;
    }
    // PSV-1726 mouse-steer: the ship faces the cursor. A finite `aim`
    // beats the binary turn on both sims (server rotates toward it at
    // agility rate, stops on alignment); explicit A/D presses override
    // the mouse so keyboard pilots keep full control.
    const mouseSteerArmed =
      Date.now() - this.lastPointerActivityAt < MOUSE_STEER_ACTIVE_MS;
    if (turn === 0 && mouseSteerArmed && this.myPredicted !== null) {
      const cam = this.scene.cameras?.main;
      const ptr = this.scene.input?.activePointer as
        | { worldX?: number; worldY?: number; updateWorldPoint?: (c: unknown) => void }
        | undefined;
      if (ptr !== undefined && typeof ptr.worldX === 'number' && typeof ptr.worldY === 'number') {
        // Camera follows the ship — refresh the pointer's world point so a
        // stationary cursor still yields correct world coords mid-flight.
        if (typeof ptr.updateWorldPoint === 'function' && cam !== undefined) {
          ptr.updateWorldPoint(cam);
        }
        const dx = ptr.worldX - this.myPredicted.x;
        const dy = ptr.worldY - this.myPredicted.y;
        // Aim deadzone: when the ship overtakes a stationary cursor, the
        // steer would flip 180° and orbit the point — thrust+near-cursor
        // read as "дёргается на месте". Inside the deadzone we hold the
        // current heading instead of chasing the pointer.
        if (dx * dx + dy * dy >= AIM_DEADZONE_PX * AIM_DEADZONE_PX) {
          this.currentInput.aim = Math.atan2(dy, dx);
        } else {
          this.currentInput.aim = undefined;
        }
      }
    } else {
      this.currentInput.aim = undefined;
    }
    // PSV-1336 Phase 1 — `fire` stays false on ShipInput. Firing is its own
    // event (`ProjectileFired`), not a flag on the movement heartbeat.
    this.currentInput.fire = false;
  }

  // ---------- fire & projectiles (PSV-1336 Phase 1) ----------

  /**
   * Cooldown-gated fire entry. Wired to both Space `keydown` and any
   * `pointerdown` on the scene. Publishes `FireRequest` with just the
   * shooter's heading; server looks up our authoritative ship position,
   * validates cooldown, and rebroadcasts the canonical `ProjectileFired`
   * which we render via the same `handleProjectileFired` callback that
   * handles remote shots.
   *
   * Skipped silently when:
   *   - cooldown hasn't elapsed (client-side gate to avoid burning
   *     network bandwidth on requests the server would reject anyway),
   *   - own ship state isn't initialized yet (we haven't spawned).
   *
   * Phase 2 (Option B): NO local prediction. The shooter sees their own
   * shot after one server round-trip (~20-50ms on test env). Trade-off:
   * slight perceptible delay vs. prediction-reconciliation complexity.
   * Revisit if visible lag becomes a player-perceived issue.
   */
  private handleFireInput(pointer?: {
    x: number;
    y: number;
    worldX?: number;
    worldY?: number;
  }): void {
    if (this.myPredicted === null) return;
    // PSV-1338 Bug 1 — Phaser fires scene-level `pointerdown` on EVERY
    // click, including ones that hit the Upgrade button's interactive
    // hit-area. Without this guard the upgrade click ALSO discharges a
    // shot. The keyboard path (Space → fireKey.on('down')) has no
    // pointer arg and falls through unaffected.
    if (
      pointer !== undefined &&
      // PSV-1911 — joystick-zone touches steer, they never shoot.
      this.touch?.swallowsFire(pointer as { x: number; id?: number }) === true
    ) {
      return;
    }
    if (
      pointer !== undefined &&
      (this.upgradePanel?.containsPointer(pointer) === true ||
        // PSV-1727 — same guard for the stat panel (bottom-left).
        this.statPanel?.containsPointer(pointer) === true ||
        // PSV-1889 — and the quest tracker; PSV-1888 — an open shop
        // swallows clicks entirely (modal backdrop covers the screen).
        this.questTracker?.containsPointer(pointer) === true ||
        this.shopModal?.isOpen === true)
    ) {
      return;
    }
    const now = Date.now();
    // PSV-1902 — the gate mirrors the SERVER's checks (per-class reload +
    // energy) instead of a flat 250ms. The old gate let clicks through
    // that the server then rejected — but the local sound/flash had
    // already played, so the shooter "heard" more shots than observers
    // ever saw (volley-count desync while strafing).
    const weapon = classById(this.myShipClassId).weapon;
    if (now - this.lastFireAt < weapon.reloadMs) return;
    if (this.myEnergy < ENERGY_COST_PER_PROJECTILE * weapon.barrels) return;
    this.lastFireAt = now;

    // PSV-1726 aim fix: a mouse shot fires TOWARD THE CURSOR (world
    // coords), not along the hull — hitting a nearby asteroid no longer
    // requires out-turning it. Space (no pointer) keeps nose-fire.
    const aimAngle =
      pointer !== undefined &&
      typeof pointer.worldX === 'number' &&
      typeof pointer.worldY === 'number'
        ? Math.atan2(
            pointer.worldY - this.myPredicted.y,
            pointer.worldX - this.myPredicted.x,
          )
        : this.myPredicted.angle;

    this.lastPointerActivityAt = Date.now();
    if (this.touch !== null && pointer !== undefined) {
      this.touchFireAim = aimAngle;
      this.touchFireAimAt = Date.now();
    }
    // PSV-1729 — instant audio feedback rides the same local trigger.
    this.sound.fire();
    // PSV-1336 Phase 4 — local-trigger muzzle flash. Fires immediately,
    // before the server round-trip, so the shooter gets instant feedback
    // even though the projectile only appears after `ProjectileFired`
    // rebroadcast (~20-50ms on test env).
    this.effects?.spawnMuzzleFlash(
      this.myPredicted.x,
      this.myPredicted.y,
      aimAngle,
    );

    // TZ-starblast 1.2: request/response RPC. The projectile itself arrives
    // via the server's `ProjectileFired` group broadcast — the RPC ack is
    // transport-level only, so we don't await it on the render path.
    void this.arenaRemote
      .Fire(FireRequest(this.myUserId, aimAngle))
      .catch((err: unknown) => {
        // Transport mid-reconnect / server hiccup — nothing was spawned
        // locally, so the dropped call just means no shot this cycle.
        // eslint-disable-next-line no-console
        console.debug(
          '[arena] Fire RPC skipped (transport unavailable):',
          err instanceof Error ? err.message : err,
        );
      });
  }

  /**
   * Listener for `ProjectileFired` broadcasts. Spawn position is adjusted
   * forward by elapsed wall-clock time since `fireTime` so a projectile
   * that took 100ms to reach us appears 100ms-of-motion into its
   * trajectory, not at the original origin — keeps positions consistent
   * across viewers despite network latency.
   *
   * Phase 2 trust model: server is the only legitimate publisher of
   * `ProjectileFired` (it does cooldown / origin / shooter-identity
   * validation before broadcasting). HOWEVER, group broadcast routes any
   * publisher's events to every subscriber — there's no server-side
   * rejection hook (PSV-1342 SDK gap). A malicious client could publish
   * a forged `ProjectileFired` directly into the group and other clients
   * would render the bogus projectile. MVP accepts this; proper anti-
   * cheat (signing / server-only sender token / membership-rejection
   * hook) is follow-up.
   *
   * Dedup by `${shooterId}-${fireTime}` is kept as defence against
   * duplicate broadcasts (e.g. reconnect rehydrate) — harmless when
   * there's no prediction to collide with.
   */
  private handleProjectileFired(raw: Record<string, unknown>): void {
    const shooterId = stringField(raw, 'shooterId');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    const angle = numberField(raw, 'angle');
    const speed = numberField(raw, 'speed');
    const fireTime = numberField(raw, 'fireTime');
    if (
      shooterId === null ||
      x === null ||
      y === null ||
      angle === null ||
      speed === null ||
      fireTime === null
    ) {
      // Malformed payload — drop silently. Phase 2 server validation will
      // make these invariants harder, but for now we just don't render.
      return;
    }

    // PSV-1726 — volley siblings share (shooterId, fireTime); the barrel
    // index keeps their keys distinct (the old key silently deduped away
    // barrels 2..n of a Trident volley).
    const barrel = numberField(raw, 'barrel') ?? 0;
    const key = projectileKey(shooterId, fireTime, barrel);
    if (this.projectiles.has(key)) return; // dedup own predictive echo

    // PSV-1902 — remote shooters get a muzzle flash too (it used to be
    // local-trigger only, so opponents' shots appeared from nowhere).
    // Once per volley (barrel 0), at the broadcast origin/heading.
    if (shooterId !== this.myUserId && barrel === 0) {
      this.effects?.spawnMuzzleFlash(x, y, angle);
    }

    const elapsedMs = Math.max(0, this.serverNow() - fireTime);
    this.spawnProjectile(shooterId, fireTime, barrel, x, y, angle, speed, elapsedMs);
  }

  /**
   * Build a `ProjectileEntry`, place the sprite at the origin advanced by
   * `elapsedMs` of motion, and register it in `this.projectiles`. Shared
   * between the predictive self-spawn (elapsedMs=0) and the broadcast
   * listener (elapsedMs=network-latency).
   */
  private spawnProjectile(
    shooterId: string,
    fireTime: number,
    barrel: number,
    originX: number,
    originY: number,
    angle: number,
    speed: number,
    elapsedMs: number,
  ): void {
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    const elapsedSec = elapsedMs / 1000;
    const x = originX + vx * elapsedSec;
    const y = originY + vy * elapsedSec;

    const sprite = this.scene.add.graphics();
    sprite.fillStyle(COLOR_PROJECTILE, 1);
    sprite.fillCircle(0, 0, PROJECTILE_RADIUS);
    sprite.setPosition(x, y);

    const key = projectileKey(shooterId, fireTime, barrel);
    this.projectiles.set(key, {
      sprite,
      vx,
      vy,
      originX,
      originY,
      fireTime,
      expiresAt: fireTime + PROJECTILE_TTL_MS,
    });
  }

  /**
   * Per-frame projectile motion + TTL cleanup. Called from `update()`.
   * Uses raw Phaser `delta` (ms) directly — projectiles are visual-only
   * in Phase 1 so frame-perfect-server-matching isn't required.
   */
  private updateProjectiles(_deltaMs: number, _now: number): void {
    if (this.projectiles.size === 0) return;
    // PSV-1902 — wall-clock (server-time) integration: position is a pure
    // function of elapsed flight time, so every screen — regardless of
    // frame rate, RAF throttling, or clock skew — renders the projectile
    // at the same point of its trajectory.
    const serverNow = this.serverNow();
    const expired: string[] = [];
    for (const [key, entry] of this.projectiles) {
      if (serverNow >= entry.expiresAt) {
        expired.push(key);
        continue;
      }
      const flightSec = Math.max(0, serverNow - entry.fireTime) / 1000;
      entry.sprite.x = entry.originX + entry.vx * flightSec;
      entry.sprite.y = entry.originY + entry.vy * flightSec;
      // PSV-1336 Phase 4 — emit one trail particle per frame at the
      // post-step position. Fades out via the EffectsManager's particle
      // age + alphaStart logic (200ms lifetime).
      this.effects?.addProjectileTrail(entry.sprite);
    }
    for (const key of expired) {
      const entry = this.projectiles.get(key);
      if (entry !== undefined) {
        entry.sprite.destroy();
        this.projectiles.delete(key);
      }
    }
  }

  // ---------- inbound ----------

  // ---------- cosmetics shop (PSV-1888) ----------

  private toggleShop(): void {
    if (this.shopModal === null) return;
    if (this.shopModal.isOpen) {
      this.shopModal.hide();
      return;
    }
    this.openShop();
  }

  private openShop(): void {
    if (this.shopModal === null) return;
    const entries = this.shopEntries.map((def) => ({
      def,
      owned: this.myCosmetics.owned.includes(def.id),
      equipped: this.myCosmetics[def.kind] === def.id,
    }));
    this.shopModal.show(entries, this.myGems);
  }

  private handleShopAction(action: ShopAction): void {
    if (action.type === 'buy') {
      void this.arenaRemote
        .PurchaseCosmetic({ itemId: action.itemId })
        .then((res) => {
          const r = res as { ok?: boolean; reason?: string; gemsAfter?: number };
          if (r.ok === true) {
            this.myCosmetics.owned.push(action.itemId);
            if (typeof r.gemsAfter === 'number') {
              this.myGems = r.gemsAfter;
              this.refreshGemsText();
              this.upgradePanel?.setGems(r.gemsAfter);
              this.statPanel?.setGems(r.gemsAfter);
            }
            this.sound.upgrade();
            this.upgradePanel?.showToast('Purchased!', 'ok');
          } else {
            this.upgradePanel?.showToast(r.reason ?? 'purchase failed', 'error');
          }
          this.openShop(); // re-render with the new state
        })
        .catch(() => this.upgradePanel?.showToast('purchase failed', 'error'));
      return;
    }
    const payload =
      action.type === 'equip'
        ? { itemId: action.itemId }
        : { itemId: '', kind: action.kind };
    void this.arenaRemote
      .EquipCosmetic(payload)
      .then((res) => {
        const r = res as { ok?: boolean; reason?: string };
        if (r.ok === true) {
          if (action.type === 'equip') {
            const def = cosmeticById(action.itemId);
            if (def !== null) this.myCosmetics[def.kind] = def.id;
          } else {
            this.myCosmetics[action.kind] = '';
          }
        } else {
          this.upgradePanel?.showToast(r.reason ?? 'equip failed', 'error');
        }
        this.openShop();
      })
      .catch(() => this.upgradePanel?.showToast('equip failed', 'error'));
  }

  /**
   * PSV-1888 — apply snapshot cosmetics to a rendered ship. The skin
   * multiply-tint wins over the class tint / remote mute; clearing falls
   * back to the class visual (own) or the remote mute tint.
   */
  private applyCosmeticsVisual(entry: ShipSprite, sh: GameStateShip): void {
    const skin = typeof sh.skin === 'string' ? sh.skin : '';
    const trail = typeof sh.trail === 'string' ? sh.trail : '';
    const death = typeof sh.death === 'string' ? sh.death : '';
    if (entry.appliedTrail !== trail) entry.appliedTrail = trail;
    if (entry.appliedDeath !== death) entry.appliedDeath = death;
    const def = cosmeticById(skin);
    if (def !== null) {
      // Re-assert EVERY snapshot, not only on change: hit-flash expiry
      // restores its saved pre-flash tint and ShipClassChanged applies
      // the class tint — both stomped an equipped skin («слетает цвет
      // после обновления», PO 2026-07-27). 20Hz compare-and-set is free.
      entry.appliedSkin = skin;
      if (entry.sprite.tintTopLeft !== def.color) entry.sprite.setTint(def.color);
      return;
    }
    if (entry.appliedSkin === skin) return;
    entry.appliedSkin = skin;
    // Cleared: restore the base visual.
    entry.sprite.clearTint();
    if (!entry.isMine) entry.sprite.setTint(COLOR_REMOTE);
    this.applyClassVisual(entry.sprite, entry.shipClass);
  }

  // ---------- touch (PSV-1911) ----------

  /** Chat entry on touch — Enter doesn't exist; a small in-canvas button
   * opens the same DOM input (the OS keyboard handles the rest). */
  private createTouchChatButton(): void {
    const cam = this.scene.cameras.main;
    this.touchChatButton = this.scene.add
      .text(cam.width - 12, cam.height - 12, '\ud83d\udcac', {
        fontSize: '26px',
        backgroundColor: '#0b101ccc',
        padding: { x: 10, y: 8 },
      })
      .setOrigin(1, 1)
      .setScrollFactor(0)
      .setDepth(25)
      .setInteractive({ useHandCursor: false });
    this.touchChatButton.on(
      'pointerdown',
      (
        _p: Phaser.Input.Pointer,
        _x: number,
        _y: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();
        this.chat?.openInput();
      },
    );
  }

  /** Touch has no hover: holding a finger ~450ms without dragging shows
   * the same tooltip the mouse pointer would; lifting hides it. */
  private armLongPressTooltips(): void {
    let downX = 0;
    let downY = 0;
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // The joystick pointer is never a long-press candidate.
      if (this.touch?.swallowsFire({ x: pointer.x, id: pointer.id }) === true) return;
      downX = pointer.x;
      downY = pointer.y;
      this.longPressTimer?.remove();
      this.longPressTimer = this.scene.time.delayedCall(450, () => {
        const cur = this.scene.input.activePointer;
        if (cur.isDown && Math.hypot(cur.x - downX, cur.y - downY) < 12) {
          this.handleHudHover({ x: cur.x, y: cur.y });
        }
      });
    });
    this.scene.input.on('pointerup', () => {
      this.longPressTimer?.remove();
      this.longPressTimer = null;
      this.tooltip?.hide();
      this.questTracker?.handleHover(null);
    });
  }

  // ---------- hover tooltips (PSV-1912) ----------

  private handleHudHover(p: { x: number; y: number }): void {
    if (this.tooltip === null) return;
    if (this.shopModal?.isOpen === true) {
      this.tooltip.hide();
      this.questTracker?.handleHover(null);
      return;
    }
    // Quest tracker owns its rows (dailies + achievements list).
    this.questTracker?.handleHover(p);

    // Leaderboard legend (top-right panel area).
    const cam = this.scene.cameras.main;
    const inLeaderboard =
      p.x >= cam.width - 230 && p.x <= cam.width - 12 && p.y >= 12 && p.y <= 200;
    // Cargo counter (top-left line).
    const inCargo = p.x >= 10 && p.x <= 330 && p.y >= 30 && p.y <= 52;
    // Buff rows (between ability line and quests — dynamic, use container y).
    const buffsY = this.buffsContainer?.y ?? -999;
    const inBuffs =
      this.activeBuffs.size > 0 &&
      p.x >= 10 &&
      p.x <= 300 &&
      p.y >= buffsY &&
      p.y <= buffsY + this.activeBuffs.size * 18;

    if (inLeaderboard) {
      this.tooltip.show(
        'LEADERBOARD\n\u2654 — bounty leader (kill for the pot!)\n\u2605 — ascension count (prestige)\n\u25c6 — cargo gems · \u2715 — kills this life',
        p.x - 240,
        p.y + 16,
      );
      return;
    }
    if (inCargo) {
      this.tooltip.show(
        'Gems ARE your cargo: they buy stats, ships and skins.\nA FULL hold stops the crystal magnet — spend to keep mining.\nDeath spills everything you carry!',
        p.x + 14,
        p.y + 10,
      );
      return;
    }
    if (inBuffs) {
      const lines = [...this.activeBuffs.keys()].map((type) => {
        const desc =
          type === 'damageBoost'
            ? '+damage while active'
            : type === 'speedBoost'
              ? '+speed while active'
              : type === 'shield'
                ? 'absorbs ALL hits while active'
                : type === 'gemMult'
                  ? 'bonus applied to that asteroid\u2019s payout'
                  : type;
        return `${type.toUpperCase()} — ${desc}`;
      });
      this.tooltip.show(`Active buffs (from asteroid bonuses):\n${lines.join('\n')}`, p.x + 14, p.y + 10);
      return;
    }
    // Nothing hovered — hide unless the quest tracker is showing one.
    if (this.questTracker === null || !this.questTracker.containsPointer(p)) {
      this.tooltip.hide();
    }
  }

  // ---------- prestige (PSV-1906) ----------

  private handleAscended(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const name = stringField(raw, 'name');
    const ascensions = numberField(raw, 'ascensions');
    if (userId === null || name === null || ascensions === null) return;
    this.ascByUserId.set(userId, ascensions);
    this.effects?.addFeedLine(`\u2605 ${name} ASCENDED (\u2605${ascensions})`, '#ffd84a');
    if (userId === this.myUserId) {
      this.sound.tierUp();
      this.upgradePanel?.showToast(
        `ASCENSION ${ascensions}! +${5 * Math.min(ascensions, 5)}% mining yield`,
        'ok',
      );
    }
  }

  /**
   * PSV-1906 — two-click ASCEND (mirrors the class-picker confirm): the
   * Upgrade button routes here at the apex. First click arms for 4s.
   */
  private ascendArmedUntil = 0;

  private requestAscend(): void {
    const now = Date.now();
    if (now > this.ascendArmedUntil) {
      this.ascendArmedUntil = now + 4_000;
      this.upgradePanel?.showToast(
        'ASCEND resets ship, stats and gems for a permanent \u2605 — click again to confirm',
        'ok',
      );
      return;
    }
    this.ascendArmedUntil = 0;
    void this.arenaRemote
      .Ascend({})
      .then((res) => {
        const r = res as { ok?: boolean; reason?: string };
        if (r.ok !== true) {
          this.upgradePanel?.showToast(r.reason ?? 'ascension rejected', 'error');
        }
      })
      .catch(() => {});
  }

  // ---------- active abilities (PSV-1905) ----------

  private requestAbility(): void {
    const def = classById(this.myShipClassId).ability;
    if (def === undefined) return; // class has no ability — silent
    if (Date.now() < this.myAbilityCooldownUntil) return; // HUD shows the timer
    void this.arenaRemote
      .UseAbility({})
      .then((res) => {
        const r = res as { ok?: boolean; reason?: string; cooldownUntil?: number };
        if (r.ok === true && typeof r.cooldownUntil === 'number') {
          this.myAbilityCooldownUntil = r.cooldownUntil;
        } else if (r.reason === 'on cooldown' && typeof r.cooldownUntil === 'number') {
          this.myAbilityCooldownUntil = r.cooldownUntil;
        }
      })
      .catch(() => {});
  }

  private handleAbilityUsed(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const ability = stringField(raw, 'ability');
    if (userId === null || ability === null) return;
    const expiresAt = numberField(raw, 'expiresAt') ?? 0;
    const ship = this.ships.get(userId);
    switch (ability) {
      case 'shieldwall':
        this.shieldWalls.set(userId, expiresAt);
        break;
      case 'drones':
        this.droneFx.set(userId, expiresAt);
        break;
      case 'dash': {
        // One-shot burst of flame out the stern.
        if (ship !== undefined) {
          for (let i = 0; i < 10; i++) {
            this.effects?.addEngineFlame(
              ship.sprite.x,
              ship.sprite.y,
              ship.sprite.rotation,
              0xffffff,
            );
          }
        }
        break;
      }
      case 'snipe':
        if (ship !== undefined) {
          this.effects?.spawnMuzzleFlash(ship.sprite.x, ship.sprite.y, ship.sprite.rotation);
        }
        break;
      default:
        break; // overdrive rides BonusApplied visuals
    }
    if (userId === this.myUserId) this.sound.upgrade();
  }

  /**
   * PSV-1902 UX — the top-left column (pilots, cargo, ability, buffs,
   * quest tracker) stacks DYNAMICALLY: fixed coordinates collided the
   * moment Overdrive added two buff rows (PO screenshot 2026-07-28).
   */
  private layoutTopLeftHud(): void {
    let y = 58; // below pilots(10) + cargo(36)
    if (this.abilityText !== null && this.abilityText.text !== '') {
      this.abilityText.setY(y);
      y += 22;
    }
    if (this.buffsContainer !== null) {
      this.buffsContainer.setY(y);
      y += this.activeBuffs.size * 18;
    }
    this.questTracker?.setBaseY(y + 6);
  }

  /** Per-frame: ability HUD line + shield-wall arcs + drone orbits. */
  private renderAbilityFx(now: number): void {
    // HUD line (own ability + cooldown).
    const def = classById(this.myShipClassId).ability;
    if (this.abilityText === null) {
      this.abilityText = this.scene.add
        .text(10, 58, '', { fontSize: '12px', color: '#7fd6ea' })
        .setScrollFactor(0)
        .setDepth(18);
    }
    if (def === undefined) {
      this.abilityText.setText('');
    } else {
      const left = this.myAbilityCooldownUntil - now;
      this.abilityText.setText(
        left > 0
          ? `SHIFT — ${def.name} (${(left / 1000).toFixed(1)}s)`
          : `SHIFT — ${def.name} (ready)`,
      );
      this.abilityText.setColor(left > 0 ? '#9aa0b4' : '#44ff7a');
    }

    // Transient per-ship effects.
    if (this.abilityFx === null) {
      this.abilityFx = this.scene.add.graphics().setDepth(6);
    }
    const g = this.abilityFx;
    g.clear();
    for (const [userId, until] of this.shieldWalls) {
      if (now >= until) {
        this.shieldWalls.delete(userId);
        continue;
      }
      const ship = this.ships.get(userId);
      if (ship === undefined) continue;
      g.lineStyle(3, 0x99ccff, 0.85);
      g.beginPath();
      g.arc(ship.sprite.x, ship.sprite.y, 34, ship.sprite.rotation - 1.31, ship.sprite.rotation + 1.31);
      g.strokePath();
    }
    for (const [userId, until] of this.droneFx) {
      if (now >= until) {
        this.droneFx.delete(userId);
        continue;
      }
      const ship = this.ships.get(userId);
      if (ship === undefined) continue;
      const phase = (now % 2000) / 2000 * Math.PI * 2;
      for (const offset of [0, Math.PI]) {
        g.fillStyle(0x55ffcc, 0.9);
        g.fillCircle(
          ship.sprite.x + Math.cos(phase + offset) * 42,
          ship.sprite.y + Math.sin(phase + offset) * 42,
          4,
        );
      }
    }
  }

  // ---------- chat (PSV-1904) ----------

  private handleChatMessage(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const name = stringField(raw, 'name');
    const text = stringField(raw, 'text');
    if (userId === null || name === null || text === null) return;
    this.chatFeed?.add(name, text, userId === this.myUserId);
  }

  // ---------- quests (PSV-1889) ----------

  private handleQuestProgress(raw: Record<string, unknown>): void {
    if (stringField(raw, 'userId') !== this.myUserId) return;
    const date = stringField(raw, 'date');
    if (date === null) return;
    const progress =
      typeof raw.progress === 'object' && raw.progress !== null
        ? (raw.progress as Partial<Record<QuestMetric, number>>)
        : {};
    const claimed = Array.isArray(raw.claimed)
      ? raw.claimed.filter((id): id is string => typeof id === 'string')
      : [];
    const unlockedIds = Array.isArray(raw.unlocked)
      ? raw.unlocked.filter((id): id is string => typeof id === 'string')
      : [];
    this.questTracker?.setState({
      date,
      progress,
      claimed,
      unlockedCount: unlockedIds.length,
      unlocked: unlockedIds,
    });
  }

  private handleAchievementUnlocked(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const name = stringField(raw, 'name');
    if (userId === null || name === null) return;
    if (userId !== this.myUserId) return; // others' unlocks stay quiet (MVP)
    this.sound.tierUp();
    this.upgradePanel?.showToast(`Achievement unlocked: ${name}!`, 'ok');
  }

  private handleDailyCompleted(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const name = stringField(raw, 'name');
    const reward = numberField(raw, 'rewardGems');
    if (userId === null || name === null || userId !== this.myUserId) return;
    this.sound.upgrade();
    this.upgradePanel?.showToast(
      `Daily complete: ${name} (+${reward ?? 0} ◆)`,
      'ok',
    );
  }

  /**
   * PSV-1902 — eligible magnet targets for CrystalManager's local sim.
   * Mirrors the server's predicate (alive + cargo space); positions are
   * the RENDERED ones so crystals visually fly to what the player sees.
   */
  private crystalMagnetTargets(): Array<{ x: number; y: number }> {
    const out: Array<{ x: number; y: number }> = [];
    for (const sh of this.lastSnapshotShips) {
      if (sh.hp <= 0) continue; // dead — server magnet skips it
      const gems = typeof sh.gems === 'number' ? sh.gems : 0;
      const cls = classById(sh.shipClass ?? '');
      if (gems >= cls.cargoCapacity) continue; // full hold
      if (sh.userId === this.myUserId) {
        if (this.myPredicted !== null) {
          out.push({ x: this.myPredicted.x, y: this.myPredicted.y });
        }
        continue;
      }
      const sprite = this.ships.get(sh.userId);
      out.push(
        sprite !== undefined
          ? { x: sprite.sprite.x, y: sprite.sprite.y }
          : { x: sh.x, y: sh.y },
      );
    }
    return out;
  }

  /** Local clock projected onto the server's (see serverClockOffsetMs). */
  private serverNow(): number {
    return Date.now() + (this.serverClockOffsetMs ?? 0);
  }

  private handleGameState(raw: Record<string, unknown>): void {
    // Defensive parse — payload comes from server but we narrow before use.
    const tRaw = raw.t;
    const tick = raw.tick;
    const ships = raw.ships;
    if (
      typeof tRaw !== 'number' ||
      typeof tick !== 'number' ||
      !Array.isArray(ships)
    ) {
      return;
    }
    const boot = raw.boot;
    if (typeof boot === 'number') {
      if (this.serverBoot !== null && boot < this.serverBoot) return; // stale process
      if (this.serverBoot !== null && boot > this.serverBoot) {
        // eslint-disable-next-line no-console
        console.info(
          '[arena] server process changed — clearing stale asteroids/crystals (field re-seeds in ~9s)',
        );
        this.asteroids?.destroy();
        this.crystalManager?.destroy();
      }
      this.serverBoot = boot;
    }
    // PSV-1902 — server-clock offset estimate (includes one-way latency;
    // constant-enough for visual timing).
    {
      const sample = tRaw - Date.now();
      this.serverClockOffsetMs =
        this.serverClockOffsetMs === null
          ? sample
          : this.serverClockOffsetMs * 0.9 + sample * 0.1;
    }
    const normalized: GameStatePayload = {
      t: tRaw,
      tick,
      ships: ships.filter(isValidShip) as GameStateShip[],
    };
    this.lastSnapshotShips = normalized.ships;
    // PSV-1902 QA — ghost pruning (see lastSeenInSnapshot).
    {
      const nowMs = Date.now();
      for (const sh of normalized.ships) this.lastSeenInSnapshot.set(sh.userId, nowMs);
      for (const [userId, ship] of this.ships) {
        if (userId === this.myUserId) continue;
        const lastSeen = this.lastSeenInSnapshot.get(userId) ?? nowMs;
        if (nowMs - lastSeen <= GHOST_PRUNE_MS) continue;
        // eslint-disable-next-line no-console
        console.info(
          `[arena] pruning ghost ship ${ship.guestName} (${userId}) — absent from snapshots ${GHOST_PRUNE_MS}ms (missed PlayerLeft)`,
        );
        this.handlePlayerLeft({ userId, reason: 'snapshot-prune' });
      }
    }

    // Bug 2 safety net (Stage 4): if our ship shows up in a GameState
    // snapshot, the server obviously knows about us — drop the loading
    // overlay even if the `PlayerJoined` we were waiting for got lost
    // in a reconnect window. Idempotent: hideLoading() is a no-op when
    // already hidden.
    //
    // Bug A safety net (Stage 5): same trigger also initializes
    // `myPredicted` and creates the own-ship sprite. Without this,
    // renderPredictedSelf stays no-op forever and own ship is invisible
    // even though server is tracking us (REPORT v12.7 Bug A).
    if (
      this.loadingText !== null ||
      this.myPredicted === null ||
      !this.ships.has(this.myUserId)
    ) {
      const meServer = normalized.ships.find(
        (s) => s.userId === this.myUserId,
      );
      if (meServer !== undefined) {
        if (this.loadingText !== null) {
          // eslint-disable-next-line no-console
          console.info('[arena] hiding loading screen — our ship seen in GameState');
          this.hideLoading();
          this.isJoined = true;
        }
        if (!this.ships.has(this.myUserId)) {
          // PlayerJoined was lost; build the sprite with placeholder
          // identity. A later PlayerJoined (e.g. on next respawn) will
          // refresh the label.
          const placeholderName = `Pilot_${this.myUserId.slice(0, 6)}`;
          const sprite = this.createShipSprite(
            this.myUserId,
            placeholderName,
            // PSV-1726 — snapshots carry the class for late joiners.
            meServer.shipClass ?? STARTER_CLASS_ID,
            meServer.x,
            meServer.y,
            meServer.angle,
            true,
          );
          this.ships.set(this.myUserId, sprite);
          // eslint-disable-next-line no-console
          console.info(
            `[arena] created own sprite from GameState (PlayerJoined missed): ${placeholderName}`,
          );
        }
        if (this.myPredicted === null) {
          // eslint-disable-next-line no-console
          console.info('[arena] init myPredicted from GameState (PlayerJoined missed)');
          this.myPredicted = {
            x: meServer.x,
            y: meServer.y,
            vx: meServer.vx,
            vy: meServer.vy,
            angle: meServer.angle,
            angularVel: meServer.angularVel,
            hp: meServer.hp,
            lastServerSnapshot: {
              x: meServer.x,
              y: meServer.y,
              vx: meServer.vx,
              vy: meServer.vy,
              angle: meServer.angle,
              angularVel: meServer.angularVel,
              tick: normalized.tick,
              t: normalized.t,
            },
          };
          // Any inputs sent during the missed window are already folded
          // into this server snapshot; clearing prevents replay from
          // double-applying them.
          this.inputHistory.length = 0;
        }
      }
    }
    this.gameStateBuffer.push({ receivedAt: Date.now(), payload: normalized });
    if (this.gameStateBuffer.length > GAMESTATE_BUFFER_MAX) {
      this.gameStateBuffer.shift();
    }

    // PSV-1727 — own energy rides the snapshot; the cap is derived
    // locally from class × levels (same formula as the server).
    const meShip = normalized.ships.find(
      (sh) => sh.userId === this.myUserId,
    );
    const meEnergy = meShip?.energy;
    // PSV-1729 QA — the snapshot's gems figure is the SERVER wallet and
    // wins over any local tally. Heals every desync path in one tick:
    // server restart (in-memory wallet reset), own death (cargo spill),
    // a missed CrystalCollected. Panels re-derive affordability from it.
    if (
      meShip !== undefined &&
      typeof meShip.gems === 'number' &&
      meShip.gems !== this.myGems
    ) {
      this.myGems = meShip.gems;
      this.refreshGemsText();
      this.upgradePanel?.setGems(meShip.gems);
      this.statPanel?.setGems(meShip.gems);
    }
    // PSV-1902 QA — the snapshot's shipClass is AUTHORITATIVE for own
    // class/tier. The legacy Player.listen upgradeTier push could
    // disagree after a restore (Ship part write silently dropped by the
    // platform → class fell back to starter while upgradeTier stayed) —
    // the panel then quoted the NEXT tier's price against a starter-cap
    // hold, an unreachable target.
    if (
      meShip !== undefined &&
      typeof meShip.shipClass === 'string' &&
      meShip.shipClass !== '' &&
      classById(meShip.shipClass).id !== this.myShipClassId
    ) {
      const def = classById(meShip.shipClass);
      this.myShipClassId = def.id;
      this.upgradePanel?.setClassId(def.id);
      this.refreshMyEffectiveStats();
    }
    if (typeof meEnergy === 'number') {
      this.myEnergy = meEnergy;
      const cap = effectiveStats(
        classById(this.myShipClassId),
        this.myStatLevels,
      ).energyCap;
      this.statPanel?.setEnergy(meEnergy, cap);
      // PSV-1729 — own-ship energy arc mirrors the panel bar.
      this.effects?.updateEnergyArc(this.myUserId, meEnergy, cap);
    }

    // PSV-1890 QA — names ride the snapshot (authoritative): heal any
    // sprite label that got created from a snapshot placeholder or
    // missed a PlayerRenamed. Own label keeps the local choice (identical
    // anyway once the server echoes it).
    for (const sh of normalized.ships) {
      const spriteEntry = this.ships.get(sh.userId);
      // PSV-1906 — prestige stars ride the snapshot; relabel on change.
      if (typeof sh.asc === 'number' && (this.ascByUserId.get(sh.userId) ?? 0) !== sh.asc) {
        this.ascByUserId.set(sh.userId, sh.asc);
        if (spriteEntry !== undefined) {
          spriteEntry.label.setText(
            this.formatLabel(
              this.labelNameFor(sh.userId, spriteEntry.guestName),
              spriteEntry.shipClass,
            ),
          );
        }
      }
      // PSV-1902 QA — the snapshot's shipClass heals the SPRITE too (not
      // just the panel): a restore sets the class server-side silently
      // (PlayerJoined fires before the spawn fetch resolves and no
      // ShipClassChanged follows), so a refreshed T3+ pilot rendered as
      // an untinted starter until the next manual tier-up.
      if (
        spriteEntry !== undefined &&
        typeof sh.shipClass === 'string' &&
        sh.shipClass !== '' &&
        spriteEntry.shipClass !== classById(sh.shipClass).id
      ) {
        spriteEntry.shipClass = classById(sh.shipClass).id;
        spriteEntry.label.setText(
          this.formatLabel(
            this.labelNameFor(sh.userId, spriteEntry.guestName),
            spriteEntry.shipClass,
          ),
        );
        this.applyClassVisual(spriteEntry.sprite, spriteEntry.shipClass);
        spriteEntry.appliedSkin = undefined; // let cosmetics re-assert
      }
      // PSV-1888 — cosmetics ride the snapshot; repaint on change.
      if (spriteEntry !== undefined) this.applyCosmeticsVisual(spriteEntry, sh);
      if (typeof sh.name !== 'string' || sh.name === '') continue;
      if (sh.userId === this.myUserId) continue;
      const entry = this.ships.get(sh.userId);
      if (entry !== undefined && entry.guestName !== sh.name) {
        entry.guestName = sh.name;
        entry.label.setText(
          this.formatLabel(this.labelNameFor(sh.userId, sh.name), entry.shipClass),
        );
      }
    }

    // PSV-1729 — leaderboard feed: gems/kills/names ride the snapshot.
    // Throttled to 2Hz — standings don't need 20Hz repaints.
    const nowLb = Date.now();
    if (this.leaderboard !== null && nowLb - this.lastLeaderboardAt > 500) {
      this.lastLeaderboardAt = nowLb;
      // PSV-1886 — track the walking bounty: #1 by gems (kills tiebreak).
      let top: GameStateShip | null = null;
      for (const sh of normalized.ships) {
        if (
          top === null ||
          (sh.gems ?? 0) > (top.gems ?? 0) ||
          ((sh.gems ?? 0) === (top.gems ?? 0) && (sh.kills ?? 0) > (top.kills ?? 0))
        ) {
          top = sh;
        }
      }
      const newLeader = top !== null && (top.gems ?? 0) > 0 ? top.userId : null;
      if (newLeader !== this.leaderUserId) {
        const prev = this.leaderUserId;
        this.leaderUserId = newLeader;
        // Crown moves: refresh both affected labels.
        for (const uid of [prev, newLeader]) {
          if (uid === null) continue;
          const entry = this.ships.get(uid);
          if (entry !== undefined) {
            entry.label.setText(
              this.formatLabel(this.labelNameFor(uid, entry.guestName), entry.shipClass),
            );
          }
        }
      }
      this.leaderboard.setEntries(
        normalized.ships.map((sh) => ({
          userId: sh.userId,
          name:
            (sh.userId === this.myUserId && this.pilotName !== null
              ? this.pilotName
              : sh.name) ??
            this.ships.get(sh.userId)?.guestName ??
            `Pilot_${sh.userId.slice(0, 6)}`,
          gems: sh.gems ?? 0,
          kills: sh.kills ?? 0,
        })),
      );
    }

    // PSV-1887 — late-join / missed-broadcast event sync: the snapshot
    // carries the active event while one runs; absence means idle.
    const evt = (raw as { event?: { type?: unknown; endsAt?: unknown } }).event;
    if (
      evt !== undefined &&
      typeof evt.type === 'string' &&
      typeof evt.endsAt === 'number'
    ) {
      if (this.activeEvent === null || this.activeEvent.type !== evt.type) {
        this.applyWorldEvent(evt.type, evt.endsAt, false);
      }
    } else if (evt === undefined && this.activeEvent !== null) {
      this.clearWorldEvent();
    }

    // Stage 5: reconcile self against authoritative server state. No-op
    // if `myPredicted` isn't initialized yet (we haven't seen our own
    // `PlayerJoined` yet) or if this snapshot doesn't list us.
    this.reconcileSelf(normalized);
  }

  private handlePlayerJoined(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    if (userId === null) return;

    // PSV-1890 — for OUR ship prefer the locally chosen name instantly:
    // the server's fallback would flash for one RPC round-trip otherwise.
    const rawGuestName =
      stringField(raw, 'guestName') ?? `Pilot_${userId.slice(0, 6)}`;
    const guestName =
      userId === this.myUserId && this.pilotName !== null && this.pilotName !== ''
        ? this.pilotName
        : rawGuestName;
    const shipClass = stringField(raw, 'shipClass') ?? 'Scout';
    const x = numberField(raw, 'spawnX') ?? numberField(raw, 'x') ?? 0;
    const y = numberField(raw, 'spawnY') ?? numberField(raw, 'y') ?? 0;
    const angle = numberField(raw, 'spawnAngle') ?? numberField(raw, 'angle') ?? 0;
    const hp = numberField(raw, 'hp') ?? this.matchConfig?.ShipMaxHP ?? 100;

    let ship = this.ships.get(userId);
    if (ship === undefined) {
      ship = this.createShipSprite(
        userId,
        guestName,
        shipClass,
        x,
        y,
        angle,
        userId === this.myUserId,
      );
      this.ships.set(userId, ship);
      // eslint-disable-next-line no-console
      console.info(`[arena] PlayerJoined: ${guestName} (${userId})`);
    } else {
      // Re-spawn / re-join after a previous PlayerLeft — refresh place
      // and label rather than tearing down + recreating sprites.
      ship.guestName = guestName;
      ship.shipClass = shipClass;
      ship.label.setText(this.formatLabel(guestName, shipClass));
      // PSV-1726 — the rejoin payload carries the persisted class.
      this.applyClassVisual(ship.sprite, shipClass);
      this.applyTransform(ship, x, y, angle);
    }

    if (userId === this.myUserId) {
      // PSV-1890 — a fresh spawn starts a fresh life for the recap stats.
      this.resetLifeStats();
      // PSV-1890 QA — the ship now exists server-side: (re)announce the
      // chosen name whenever the SERVER greeted us with the fallback.
      // Compare against rawGuestName — `guestName` is already the local
      // preference for own ships, so comparing it would always match
      // and the announce would never fire (the "никто не видит имён" bug).
      // Covers first join, TTL-evict respawns AND server restarts.
      if (
        this.pilotName !== null &&
        this.pilotName !== '' &&
        rawGuestName !== this.pilotName
      ) {
        this.announcePilotName(this.pilotName);
      }
      // Stage 5: (re)initialize prediction state from server-reported spawn.
      // Fires on first join AND on respawn (after TTL eviction). The fresh
      // baseline + zero velocity matches how the server's `spawnShip` sets
      // up the ship — reconciliation will keep us in sync from here.
      this.myPredicted = {
        x,
        y,
        vx: 0,
        vy: 0,
        angle,
        angularVel: 0,
        hp,
        lastServerSnapshot: null,
      };
      // Old unacked inputs from before this respawn are physically stale —
      // server won't replay them onto the fresh ship.
      this.inputHistory.length = 0;

      if (!this.isJoined) {
        this.isJoined = true;
        this.hideLoading();
        // eslint-disable-next-line no-console
        console.info(`[arena] joined as ${guestName}`);
      }
    }
  }

  private handlePlayerLeft(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    if (userId === null) return;
    const reason = stringField(raw, 'reason') ?? 'unknown';

    const ship = this.ships.get(userId);
    if (ship === undefined) return;
    ship.sprite.destroy();
    ship.label.destroy();
    this.ships.delete(userId);
    this.effects?.detachShipHpBar(userId);
    this.hpMaxByUserId.delete(userId);
    // eslint-disable-next-line no-console
    console.info(`[arena] PlayerLeft: ${ship.guestName} (${userId}, ${reason})`);

    if (userId === this.myUserId) {
      // Server evicted us (TTL). Next `PlayerJoined` for our userId will
      // re-initialize `myPredicted` from a fresh spawn — drop now so the
      // physics tick doesn't keep drifting a state that no longer exists.
      this.myPredicted = null;
      this.inputHistory.length = 0;
    }
  }

  /**
   * PSV-1336 Phase 4 — ShipDamaged: server broadcasts post-damage HP for
   * every projectile→ship hit. Drives HP bar refresh + white-flash on
   * the victim sprite + hit-spark particle burst at projectile impact
   * point. We don't have the impact (x, y) on the wire (PSV-1336 spec
   * scoped it to ProjectileHit) so spark spawns at the victim sprite's
   * current world position — close enough at melee range.
   */
  private handleShipDamaged(raw: Record<string, unknown>): void {
    if (this.effects === null) return;
    const userId = stringField(raw, 'userId');
    const hp = numberField(raw, 'hp');
    const hpMax = numberField(raw, 'hpMax');
    if (userId === null || hp === null || hpMax === null) return;
    this.hpMaxByUserId.set(userId, hpMax);
    this.effects.updateHpBar(userId, hp, hpMax);
    const victimSprite = this.ships.get(userId);
    if (victimSprite !== undefined) {
      this.effects.spawnHitFlash(victimSprite.sprite, userId);
      this.effects.spawnHitSpark(victimSprite.sprite.x, victimSprite.sprite.y);
    }
    // PSV-1729 — feel: sound for every hit, camera shake for OWN pain.
    this.sound.hit();
    if (userId === this.myUserId) {
      this.scene.cameras.main.shake(120, 0.006);
    }
  }

  /**
   * PSV-1336 Phase 4 — ShipKilled: large coloured burst + ring at the
   * death position carried on the event payload. Hides the victim's
   * sprite (will be re-shown on ShipRespawned). Adds a kill feed line;
   * if the killed user is us, also shows the YOU DIED overlay.
   */
  private handleShipKilled(raw: Record<string, unknown>): void {
    if (this.effects === null) return;
    const userId = stringField(raw, 'userId');
    const killerId = stringField(raw, 'killerId');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    if (userId === null || killerId === null || x === null || y === null) {
      return;
    }
    // PSV-1888 — the victim's equipped death effect recolors the debris.
    const deathFx = cosmeticById(this.ships.get(userId)?.appliedDeath);
    this.effects.spawnDeathExplosion(x, y, deathFx?.color);
    const victim = this.ships.get(userId);
    if (victim !== undefined) {
      victim.sprite.setVisible(false);
      victim.label.setVisible(false);
    }
    this.effects.updateHpBar(userId, 0, this.hpMaxByUserId.get(userId) ?? ArenaController.DEFAULT_HP_MAX);

    const killerName =
      this.ships.get(killerId)?.guestName ?? `Pilot_${killerId.slice(0, 6)}`;
    const victimName =
      this.ships.get(userId)?.guestName ?? `Pilot_${userId.slice(0, 6)}`;
    const isOwnKill = killerId === this.myUserId;
    const isOwnDeath = userId === this.myUserId;
    this.effects.addKillFeedEntry(killerName, victimName, isOwnKill, isOwnDeath);

    // PSV-1729 — feel: explosions are loud, own death shakes the screen,
    // own kills get an unmissable toast (the spilled cargo is the loot).
    this.sound.shipExplosion();
    if (isOwnKill) {
      this.lifeKills += 1; // PSV-1890 recap
      this.upgradePanel?.showToast(`You destroyed ${victimName}! Scoop the cargo!`, 'ok');
    }
    if (isOwnDeath) {
      this.sound.death();
      this.scene.cameras.main.shake(300, 0.012);
      this.effects.showYouDied();
      this.showDeathRecap(
        killerName,
        killerId,
        numberField(raw, 'bountyDropped') ?? 0,
        numberField(raw, 'streakEnded') ?? 0,
      );
    }
  }

  /**
   * PSV-1336 Phase 4 — ShipRespawned: server resurrected the ship 3s
   * after the kill. Re-show the sprite (kept around but hidden during
   * the respawn window), refresh HP bar to full, and clear the YOU DIED
   * overlay if it was up.
   */
  private handleShipRespawned(raw: Record<string, unknown>): void {
    if (this.effects === null) return;
    const userId = stringField(raw, 'userId');
    const hp = numberField(raw, 'hp');
    if (userId === null || hp === null) return;
    const hpMax = this.hpMaxByUserId.get(userId) ?? hp;
    this.effects.updateHpBar(userId, hp, hpMax);
    const ship = this.ships.get(userId);
    if (ship !== undefined) {
      ship.sprite.setVisible(true);
      ship.label.setVisible(true);
    }
    if (userId === this.myUserId) {
      this.effects.hideYouDied();
      // PSV-1890 — recap closes when the server actually resurrects us.
      this.deathRecap?.hide();
      this.deathRecap?.setPlayAgainEnabled(true);
      this.resetLifeStats();
    }
  }

  // ---------- asteroids + bonuses (PSV-1337) ----------

  private handleAsteroidSpawned(raw: Record<string, unknown>): void {
    if (this.asteroids === null) return;
    const id = stringField(raw, 'id');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    const radius = numberField(raw, 'radius');
    const hpMax = numberField(raw, 'hpMax');
    const hp = numberField(raw, 'hp');
    const bonusType = typeof raw.bonusType === 'string' ? raw.bonusType : '';
    if (
      id === null ||
      x === null ||
      y === null ||
      radius === null ||
      hpMax === null ||
      hp === null
    ) {
      return;
    }
    this.asteroids.spawnAsteroid({ id, x, y, radius, hpMax, hp, bonusType });
  }

  /**
   * PSV-1726 projectile-lifecycle sync — the server resolved a projectile
   * (ship hit / asteroid hit / shield block): despawn its visual at the
   * impact point instead of letting it fly through the target for the
   * rest of its TTL. Fields are optional on older payloads — silently
   * no-op when identity is absent.
   */
  private removeProjectileVisual(
    raw: Record<string, unknown>,
    impactXField = 'x',
    impactYField = 'y',
  ): void {
    const shooterId = stringField(raw, 'shooterId');
    const fireTime = numberField(raw, 'fireTime');
    const barrel = numberField(raw, 'barrel');
    if (shooterId === null || fireTime === null || barrel === null) return;
    const key = projectileKey(shooterId, fireTime, barrel);
    const entry = this.projectiles.get(key);
    if (entry === undefined) return;
    entry.sprite.destroy();
    this.projectiles.delete(key);
    const ix = numberField(raw, impactXField);
    const iy = numberField(raw, impactYField);
    if (ix !== null && iy !== null) this.effects?.spawnHitSpark(ix, iy);
  }

  /** ProjectileHit — a shot connected with a ship somewhere in the arena. */
  private handleProjectileHit(raw: Record<string, unknown>): void {
    this.removeProjectileVisual(raw);
  }

  private handleAsteroidUpdated(raw: Record<string, unknown>): void {
    this.removeProjectileVisual(raw, 'hitX', 'hitY');
    if (this.asteroids === null) return;
    const id = stringField(raw, 'id');
    const hp = numberField(raw, 'hp');
    if (id === null || hp === null) return;
    this.asteroids.updateAsteroidHp(id, hp);
  }

  private handleAsteroidMoved(raw: Record<string, unknown>): void {
    if (this.asteroids === null) return;
    const id = stringField(raw, 'id');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    if (id === null || x === null || y === null) return;
    this.asteroids.moveAsteroid(id, x, y);
  }

  private handleAsteroidDestroyed(raw: Record<string, unknown>): void {
    this.removeProjectileVisual(raw, 'hitX', 'hitY');
    if (this.asteroids === null) return;
    const id = stringField(raw, 'id');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    const gemValue = numberField(raw, 'gemValue');
    const bonusType = typeof raw.bonusType === 'string' ? raw.bonusType : '';
    const shooterId = stringField(raw, 'shooterId');
    if (
      id === null ||
      x === null ||
      y === null ||
      gemValue === null ||
      shooterId === null
    ) {
      return;
    }
    const isOwnShot = shooterId === this.myUserId;
    this.asteroids.destroyAsteroid(id, x, y, gemValue, bonusType, isOwnShot);
    // PSV-1728 — NO instant gem credit anymore: the payout scatters as
    // crystals (CrystalBurst) and the wallet moves only on
    // CrystalCollected. gemValue stays on this event for FX text.
    this.sound.asteroidCrack();
  }

  // ---------- crystals (PSV-1728) ----------

  private handleCrystalBurst(raw: Record<string, unknown>): void {
    const list = Array.isArray(raw.crystals) ? raw.crystals : null;
    if (list === null || this.crystalManager === null) return;
    const parsed = list.filter(
      (c): c is { id: string; x: number; y: number; vx: number; vy: number; value: number; expiresAt: number } =>
        typeof c === 'object' &&
        c !== null &&
        typeof (c as { id?: unknown }).id === 'string' &&
        typeof (c as { x?: unknown }).x === 'number' &&
        typeof (c as { y?: unknown }).y === 'number' &&
        typeof (c as { vx?: unknown }).vx === 'number' &&
        typeof (c as { vy?: unknown }).vy === 'number' &&
        typeof (c as { value?: unknown }).value === 'number' &&
        typeof (c as { expiresAt?: unknown }).expiresAt === 'number',
    );
    this.crystalManager.spawnBurst(parsed);
  }

  /**
   * CrystalCollected — authoritative pickup. Everyone tweens the
   * crystal into the collector; the collector's wallet/HUD sync from
   * `gemsAfter` (covers cargo-cap clamping — no client-side math).
   */
  private handleCrystalCollected(raw: Record<string, unknown>): void {
    const id = stringField(raw, 'id');
    const userId = stringField(raw, 'userId');
    if (id === null || userId === null) return;
    this.crystalManager?.collect(id, userId);
    if (userId !== this.myUserId) return;
    this.sound.crystalPickup();
    const value = numberField(raw, 'value');
    if (value !== null && value > 0) this.lifeGems += value; // PSV-1890 recap
    if (value !== null && value > 0 && this.myPredicted !== null) {
      // PSV-1729 — floating "+N ◆" at the hull: the pickup dopamine hit.
      this.spawnScorePopup(this.myPredicted.x, this.myPredicted.y - 30, value);
    }
    const gemsAfter = numberField(raw, 'gemsAfter');
    if (gemsAfter !== null) {
      this.myGems = gemsAfter;
      this.refreshGemsText();
      this.upgradePanel?.setGems(gemsAfter);
      this.statPanel?.setGems(gemsAfter);
    }
  }

  /** PSV-1729 — small floating score text that drifts up and fades. */
  private spawnScorePopup(x: number, y: number, value: number): void {
    const text = this.scene.add
      .text(x, y, `+${value} \u25c6`, {
        fontSize: '14px',
        color: '#ffd84a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(12);
    this.scene.tweens.add({
      targets: text,
      y: y - 34,
      alpha: 0,
      duration: 750,
      onComplete: () => text.destroy(),
    });
  }

  private handleCrystalsExpired(raw: Record<string, unknown>): void {
    const ids = Array.isArray(raw.ids)
      ? raw.ids.filter((v): v is string => typeof v === 'string')
      : [];
    if (ids.length > 0) this.crystalManager?.expire(ids);
  }

  private handleBonusApplied(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const bonusType = typeof raw.bonusType === 'string' ? raw.bonusType : '';
    const expiresAt = numberField(raw, 'expiresAt');
    if (userId === null || bonusType === '' || expiresAt === null) return;
    if (userId !== this.myUserId) return; // HUD only tracks own buffs

    // Heal / gemMult are instant — `expiresAt === 0`. Don't add to the
    // active buffs list; the client's HUD only shows timed buffs.
    if (expiresAt === 0) return;
    this.addOrReplaceBuff(bonusType, expiresAt);
  }

  private handleBuffExpired(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const bonusType = typeof raw.bonusType === 'string' ? raw.bonusType : '';
    if (userId === null || bonusType === '') return;
    if (userId !== this.myUserId) return;
    const existing = this.activeBuffs.get(bonusType);
    if (existing !== undefined) {
      existing.text.destroy();
      this.activeBuffs.delete(bonusType);
    }
  }

  private handleShieldBlocked(raw: Record<string, unknown>): void {
    this.removeProjectileVisual(raw);
    const userId = stringField(raw, 'userId');
    const x = numberField(raw, 'x');
    const y = numberField(raw, 'y');
    if (userId === null || x === null || y === null) return;
    // Expanding blue ring at the absorb point. Lightweight ad-hoc effect
    // — no need to route through EffectsManager.
    const g = this.scene.add.graphics();
    g.lineStyle(3, 0x4488ff, 1);
    g.strokeCircle(0, 0, 10);
    g.setPosition(x, y);
    g.setDepth(4);
    this.scene.tweens.add({
      targets: g,
      scale: 6,
      alpha: 0,
      duration: 450,
      ease: 'Quad.easeOut',
      onComplete: () => g.destroy(),
    });
  }

  // ---------- upgrades (PSV-1338) ----------

  /**
   * Open a `Player.listen()` subscription on the local userId so the
   * UpgradePanel + gems HUD update reactively when the server mutates
   * `Gems` / `upgradeTier`. Mirrors the matchConfig hot-reload pattern
   * (Stage 7 Track A) but scoped to a single Player record.
   */
  private setupPlayerListen(): void {
    if (this.playerListenHandle !== null) return;
    try {
      this.playerListenHandle = this.client
        .model<Record<string, unknown>>('Player')
        .key(this.myUserId)
        .fields('State', 'Gems')
        .listen();
      this.playerListenHandle.wait('update', (event) => {
        const next = event.new as Record<string, unknown> | undefined;
        if (next === undefined) return;
        const gems = typeof next.Gems === 'number' ? next.Gems : null;
        if (gems !== null) {
          this.myGems = gems;
          this.refreshGemsText();
          this.upgradePanel?.setGems(gems);
          this.statPanel?.setGems(gems);
        }
        // PSV-1902 QA — the legacy upgradeTier push no longer drives the
        // panel: after a lossy restore it contradicted the authoritative
        // snapshot class (see handleGameState). Gems handling above stays.
      });
      // eslint-disable-next-line no-console
      console.info('[upgrade] Player.listen subscribed for own userId');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        '[upgrade] Player.listen failed — HUD will refresh only via UpgradeApplied broadcasts:',
        err,
      );
      this.playerListenHandle = null;
    }
  }

  // PSV-1726 — legacy linear-upgrade path, no longer wired to the
  // Upgrade button (which opens the class picker now). Kept while the
  // server still serves the Upgrade RPC / broadcasts; `protected` (not
  // `private`) so tsc's unused-private-member check doesn't flag it.
  protected publishUpgradeRequest(): void {
    // TZ-starblast 1.2: request/response RPC. Outcome arrives as the
    // UpgradeApplied / UpgradeRejected group broadcast (same contract as
    // before) — the RPC result is a transport ack only.
    void this.arenaRemote
      .Upgrade(UpgradeRequest(this.myUserId))
      .then(() => {
        // eslint-disable-next-line no-console
        console.info('[upgrade] Upgrade RPC sent');
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.debug(
          '[upgrade] Upgrade RPC skipped (transport unavailable):',
          err instanceof Error ? err.message : err,
        );
      });
  }

  private handleUpgradeApplied(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const newTier = numberField(raw, 'newTier');
    const gemsAfter = numberField(raw, 'gemsAfter');
    if (userId === null || newTier === null || gemsAfter === null) return;
    if (userId !== this.myUserId) return; // others' upgrades are silent here
    // Refresh local state immediately — Player.listen push lands ~ms
    // later but a deliberate setGems refresh here avoids a perceptible
    // delay between toast + HUD update.
    this.myGems = gemsAfter;
    this.upgradePanel?.setGems(gemsAfter);
    // Legacy PSV-1338 cap — superseded by ShipClassChanged (PSV-1726);
    // the panel's class (and therefore tier) only moves on that event.
    this.myEngineMax = statsForTier(newTier).engineMax;
    this.refreshGemsText();
    this.upgradePanel?.showToast(`Upgraded to tier ${newTier}!`, 'ok');
  }

  private handleUpgradeRejected(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const reason = typeof raw.reason === 'string' ? raw.reason : '';
    if (userId === null || reason === '') return;
    if (userId !== this.myUserId) return;
    const msg =
      reason === 'NOT_ENOUGH_GEMS'
        ? 'Not enough gems!'
        : reason === 'MAX_TIER'
          ? 'Max tier reached!'
          : reason === 'NO_PLAYER'
            ? 'Player record missing'
            : `Upgrade rejected: ${reason}`;
    this.upgradePanel?.showToast(msg, 'error');
  }

  // ---------- ship classes (PSV-1726) ----------

  /**
   * Upgrade-button entry: offer the next tier's classes in the picker
   * modal. Affordability is rendered per card (the panel's own gating
   * already required `gems >= cost` to fire the click, but the picker
   * re-checks so a stale panel can't slip an unaffordable pick through).
   */
  private openClassPicker(): void {
    // PSV-1906 — at the apex the Upgrade button becomes ASCEND.
    if (classById(this.myShipClassId).tier === MAX_CLASS_TIER) {
      this.requestAscend();
      return;
    }
    // PSV-1905 — DAG-aware: only children reachable from MY node.
    const choices = nextChoicesFor(this.myShipClassId);
    if (choices.length === 0) {
      this.upgradePanel?.showToast('Max tier reached', 'error');
      return;
    }
    this.classPicker?.show(choices, this.myGems);
  }

  /** Picker `onPick` → ChooseShipClass RPC. Outcome arrives via broadcast. */
  private requestClassChange(classId: string): void {
    void this.arenaRemote.ChooseShipClass({ classId }).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.debug(
        '[class] ChooseShipClass RPC skipped (transport unavailable):',
        err instanceof Error ? err.message : err,
      );
    });
  }

  /**
   * ShipClassChanged — authoritative tier-up outcome for ANY player.
   * Everyone retints/rescales the sprite and stores the class on the ship
   * entry (so respawn label refreshes keep it); for our own userId we
   * additionally update prediction stats (agility / engineMax), the
   * upgrade panel (class tier + gems), and toast the new class name.
   */
  private handleShipClassChanged(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const classId = stringField(raw, 'classId');
    if (userId === null || classId === null) return;
    const def = classById(classId);

    const ship = this.ships.get(userId);
    if (ship !== undefined) {
      ship.shipClass = def.id;
      ship.label.setText(this.formatLabel(ship.guestName, def.id));
      this.applyClassVisual(ship.sprite, def.id);
    }
    // hpMax rides on the event — cache it so HP bars render the right
    // ratio before the next ShipDamaged delivers it (same cache the
    // combat handlers use).
    const hpMax = numberField(raw, 'hpMax');
    const hp = numberField(raw, 'hp');
    if (hpMax !== null) {
      this.hpMaxByUserId.set(userId, hpMax);
      if (hp !== null) this.effects?.updateHpBar(userId, hp, hpMax);
    }

    if (userId !== this.myUserId) return;
    this.myShipClassId = def.id;
    // PSV-1727 — ascension resets stat levels (server does the same);
    // effective stats collapse back to the new class's base values.
    this.myStatLevels = emptyStatLevels();
    this.refreshMyEffectiveStats();
    this.upgradePanel?.setClassId(def.id);
    const gemsAfter = numberField(raw, 'gemsAfter');
    if (gemsAfter !== null) {
      this.myGems = gemsAfter;
      this.refreshGemsText();
      this.upgradePanel?.setGems(gemsAfter);
      this.statPanel?.setGems(gemsAfter);
    }
    this.sound.tierUp();
    this.upgradePanel?.showToast(`Now flying ${def.name}!`, 'ok');
  }

  /** ShipClassRejected — server denied OUR tier-up attempt. Toast only. */
  private handleShipClassRejected(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    if (userId === null || userId !== this.myUserId) return;
    const reason =
      typeof raw.reason === 'string' && raw.reason !== ''
        ? raw.reason
        : 'unknown';
    // PSV-1727 — human wording for the common gates.
    const msg =
      reason === 'stats not maxed'
        ? 'Max out all 8 stats to unlock the next tier!'
        : reason === 'not enough gems'
          ? 'Not enough gems for the next tier!'
          : `Class change rejected: ${reason}`;
    this.upgradePanel?.showToast(msg, 'error');
  }

  // ---------- per-stat upgrades (PSV-1727) ----------

  /**
   * Recompute my prediction caps + stat panel from class × stat levels.
   * Mirrors the server's `applyEffectiveStats` — both sims must derive
   * speed/agility identically or reconciliation starts fighting the
   * predictor (the PSV-1785 engineMax lesson).
   */
  private refreshMyEffectiveStats(): void {
    // PSV-1906 balance — stat prices scale with the ship tier.
    this.statPanel?.setClassTier(classById(this.myShipClassId).tier);
    const eff = effectiveStats(classById(this.myShipClassId), this.myStatLevels);
    this.myAgility = eff.agility;
    this.myEngineMax = eff.engineMax;
    this.statPanel?.setLevels(this.myStatLevels);
    // PSV-1727 — keep the tier-up button honest: it stays visually
    // locked (with a progress hint) until the stat board is maxed.
    const total = STAT_ORDER.reduce((n, id) => n + this.myStatLevels[id], 0);
    this.upgradePanel?.setStatsProgress(total);
  }

  /** Hotkey/panel entry → UpgradeStat RPC. Outcome arrives via broadcast. */
  private requestStatUpgrade(statId: StatId): void {
    void this.arenaRemote.UpgradeStat({ statId }).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.debug(
        '[stats] UpgradeStat RPC skipped (transport unavailable):',
        err instanceof Error ? err.message : err,
      );
    });
  }

  /**
   * StatUpgraded — authoritative purchase outcome. hpMax rides along for
   * the HP-bar cache (shieldCap purchases raise it); everything else is
   * own-ship HUD + prediction bookkeeping.
   */
  private handleStatUpgraded(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const statId = stringField(raw, 'statId');
    const level = numberField(raw, 'level');
    if (userId === null || statId === null || level === null) return;
    const hpMax = numberField(raw, 'hpMax');
    if (hpMax !== null) this.hpMaxByUserId.set(userId, hpMax);
    if (userId !== this.myUserId) return;
    if (!STAT_ORDER.includes(statId as StatId)) return;

    this.myStatLevels[statId as StatId] = level;
    this.refreshMyEffectiveStats();
    const gemsAfter = numberField(raw, 'gemsAfter');
    if (gemsAfter !== null) {
      this.myGems = gemsAfter;
      this.refreshGemsText();
      this.upgradePanel?.setGems(gemsAfter);
      this.statPanel?.setGems(gemsAfter);
    }
    this.sound.upgrade();
    // Quiet by design — the pips lighting up IS the feedback. The one
    // moment worth shouting about is unlocking the next class tier.
    if (raw.allMaxed === true) {
      this.upgradePanel?.showToast('All stats maxed — tier up available!', 'ok');
    }
  }

  /** StatUpgradeRejected — server denied OUR purchase. Toast only. */
  private handleStatUpgradeRejected(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    if (userId === null || userId !== this.myUserId) return;
    const reason =
      typeof raw.reason === 'string' && raw.reason !== ''
        ? raw.reason
        : 'unknown';
    this.upgradePanel?.showToast(`Upgrade rejected: ${reason}`, 'error');
  }

  // ---------- pilot identity + death loop (PSV-1890) ----------

  /**
   * Send the chosen name; server sanitizes + echoes PlayerRenamed.
   * Retries a couple of times on the spawn race ('ship not active' —
   * our PlayerJoined can beat the server's ships-map insert visibility
   * across the uplink round-trip).
   */
  private announcePilotName(name: string, attempt = 0): void {
    void this.arenaRemote
      .SetName({ name })
      .then((res) => {
        const ok = (res as { ok?: unknown } | null)?.ok === true;
        const reason = (res as { reason?: unknown } | null)?.reason;
        if (!ok && reason === 'ship not active' && attempt < 3) {
          this.scene.time.delayedCall(700, () =>
            this.announcePilotName(name, attempt + 1),
          );
        }
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.debug(
          '[identity] SetName RPC skipped (transport unavailable):',
          err instanceof Error ? err.message : err,
        );
      });
  }

  /** PlayerRenamed — refresh every surface that shows the name. */
  private handlePlayerRenamed(raw: Record<string, unknown>): void {
    const userId = stringField(raw, 'userId');
    const name = typeof raw.name === 'string' ? raw.name : null;
    if (userId === null || name === null || name === '') return;
    const ship = this.ships.get(userId);
    if (ship !== undefined) {
      ship.guestName = name;
      ship.label.setText(this.formatLabel(name, ship.shipClass));
    }
    // Leaderboard picks the new name up on its next snapshot repaint.
  }

  /** PLAY AGAIN — ask the server to collapse the respawn delay. */
  private requestRespawnNow(): void {
    this.deathRecap?.setPlayAgainEnabled(false);
    void this.arenaRemote
      .RespawnNow({})
      .catch((err: unknown) => {
        this.deathRecap?.setPlayAgainEnabled(true);
        // eslint-disable-next-line no-console
        console.debug(
          '[identity] RespawnNow RPC failed:',
          err instanceof Error ? err.message : err,
        );
      });
  }

  /** "Change name" from the recap — re-open the DOM entry overlay. */
  private promptRename(): void {
    // Lazy import keeps the DOM overlay out of the hot path; the module
    // is already loaded by ArenaScene, so this resolves synchronously.
    void import('../../ui/EntryOverlay').then(({ EntryOverlay }) => {
      const overlay = new EntryOverlay({
        initialName: this.pilotName ?? '',
        onPlay: (name) => {
          overlay.destroy();
          this.pilotName = name;
          this.announcePilotName(name);
        },
      });
      overlay.show();
    });
  }

  /** PSV-1886 — the #1 pilot wears a crown in the on-canvas label. */
  private labelNameFor(userId: string, name: string): string {
    // PSV-1906 — prestige stars prefix every name surface; the bounty
    // leader's crown stacks in front.
    const stars = ascensionStars(this.ascByUserId.get(userId) ?? 0);
    const starred = stars !== '' ? `${stars} ${name}` : name;
    return userId === this.leaderUserId ? `\u2654 ${starred}` : starred;
  }

  /** StreakAnnounce — arena-wide: feed line + toast + stinger. */
  private handleStreakAnnounce(raw: Record<string, unknown>): void {
    const name = typeof raw.name === 'string' ? raw.name : null;
    const label = typeof raw.label === 'string' ? raw.label : null;
    const streak = numberField(raw, 'streak');
    if (name === null || label === null || streak === null) return;
    this.effects?.addFeedLine(`\u2605 ${name} ${label}`, '#ffd84a');
    const userId = stringField(raw, 'userId');
    this.upgradePanel?.showToast(
      userId === this.myUserId
        ? `You ${label} (${streak} kills)`
        : `${name} ${label}`,
      userId === this.myUserId ? 'ok' : 'error',
    );
    this.sound.streak();
  }

  // ---------- world events (PSV-1887) ----------

  private handleWorldEvent(raw: Record<string, unknown>): void {
    const type = typeof raw.event === 'string' ? raw.event : null;
    const phase = typeof raw.phase === 'string' ? raw.phase : null;
    const endsAt = numberField(raw, 'endsAt');
    if (type === null || phase === null) return;
    if (phase === 'end') {
      this.clearWorldEvent();
      return;
    }
    this.applyWorldEvent(type, endsAt ?? Date.now(), true);
  }

  /** Enter event state: banner + stinger (fresh only) + zone repaint. */
  private applyWorldEvent(type: string, endsAt: number, fresh: boolean): void {
    this.activeEvent = { type, endsAt };
    if (this.eventBanner === null) {
      const cam = this.scene.cameras.main;
      this.eventBanner = this.scene.add
        .text(cam.centerX, 46, '', {
          fontSize: '26px',
          color: '#ffd84a',
          fontStyle: 'bold',
          backgroundColor: '#000000aa',
          padding: { x: 18, y: 8 },
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(26);
    }
    this.eventBanner.setColor(type === 'goldRush' ? '#ffd84a' : '#ff5577');
    this.eventBanner.setVisible(true);
    this.updateEventBanner();
    this.redrawHotZoneGlow();
    if (fresh) {
      if (type === 'goldRush') this.sound.tierUp();
      else this.sound.streak();
      this.effects?.addFeedLine(
        type === 'goldRush' ? '\u2726 GOLD RUSH in the core!' : '\u26a0 ASTEROID STORM!',
        type === 'goldRush' ? '#ffd84a' : '#ff5577',
      );
    }
  }

  private clearWorldEvent(): void {
    this.activeEvent = null;
    this.eventBanner?.setVisible(false);
    this.redrawHotZoneGlow();
  }

  /** Countdown text; auto-clears if the end broadcast got lost. */
  private updateEventBanner(): void {
    if (this.activeEvent === null || this.eventBanner === null) return;
    const leftMs = this.activeEvent.endsAt - Date.now();
    if (leftMs <= 0) {
      this.clearWorldEvent();
      return;
    }
    const label =
      this.activeEvent.type === 'goldRush' ? 'GOLD RUSH' : 'ASTEROID STORM';
    const sec = Math.ceil(leftMs / 1000);
    const text = `${label}  ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
    if (this.eventBanner.text !== text) this.eventBanner.setText(text);
  }

  /** Gold disc marking the rich belt — PO call (2026-07-29): shown ONLY
   * while a world event runs; outside events the belt is invisible. */
  private redrawHotZoneGlow(): void {
    const g = this.hotZoneGlow;
    if (g === null) return;
    const radius = this.matchConfig?.HotZoneRadius ?? 0;
    g.clear();
    const hot = this.activeEvent !== null;
    if (radius <= 0 || !hot) {
      this.hotZoneLabel?.setVisible(false);
      return;
    }
    g.fillStyle(0xffd84a, 0.12);
    g.fillCircle(0, 0, radius);
    g.lineStyle(2, 0xffd84a, 0.55);
    g.strokeCircle(0, 0, radius);
    this.hotZoneLabel
      ?.setText('HOT ZONE \u00d72.5 \u25c6')
      .setPosition(0, -radius - 10)
      .setAlpha(0.95)
      .setVisible(true);
  }

  private resetLifeStats(): void {
    this.lifeStartAt = Date.now();
    this.lifeGems = 0;
    this.lifeKills = 0;
  }

  /**
   * Assemble + show the recap. Session bests live in localStorage so a
   * reload keeps the bar to beat ("New personal best!" moment).
   */
  private showDeathRecap(
    killerName: string,
    killerId: string,
    bountyDropped = 0,
    streakEnded = 0,
  ): void {
    const survivalSec = Math.max(
      0,
      Math.round((Date.now() - this.lifeStartAt) / 1000),
    );
    const gemsLost = this.myGems; // captured before the snapshot zero-sync
    const bestCargo = Number(localStorage.getItem('starblast.bestCargo') ?? '0');
    const bestKills = Number(localStorage.getItem('starblast.bestKills') ?? '0');
    const newBestCargo = this.lifeGems > bestCargo;
    const newBestKills = this.lifeKills > bestKills;
    if (newBestCargo) {
      localStorage.setItem('starblast.bestCargo', String(this.lifeGems));
    }
    if (newBestKills) {
      localStorage.setItem('starblast.bestKills', String(this.lifeKills));
    }
    this.deathRecap?.setPlayAgainEnabled(true);
    this.deathRecap?.show({
      killerName: killerId === 'asteroid' ? 'asteroid' : killerName,
      survivalSec,
      kills: this.lifeKills,
      gemsCollected: this.lifeGems,
      gemsLost,
      newBestCargo,
      newBestKills,
      bountyDropped,
      streakEnded,
    });
  }

  /** First-session-only onboarding toasts (move → fire → scoop). */
  private maybeShowFirstSessionHints(): void {
    if (hintsAlreadyShown()) return;
    markHintsShown();
    const hints: Array<[number, string]> = [
      [2_000, 'Move with WASD or arrows'],
      [6_500, 'Fire with mouse or Space — ship aims at your cursor'],
      [11_000, 'Shatter asteroids and scoop the crystals ◆'],
    ];
    for (const [delay, text] of hints) {
      this.scene.time.delayedCall(delay, () => {
        this.upgradePanel?.showToast(text, 'ok');
      });
    }
  }

  // ---------- HUD helpers (PSV-1337) ----------

  private createGemsText(): void {
    if (this.gemsText !== null) return;
    // Seed from the cached Player record (loaded by BootScene) so the
    // counter starts at the persisted value, not 0. `getPlayer()` may
    // throw if called pre-BootScene; defensive try/catch keeps Arena
    // resilient to a partial-load scenario.
    try {
      this.myGems = getPlayer().Gems;
    } catch {
      this.myGems = 0;
    }
    this.gemsText = this.scene.add
      .text(10, 36, this.formatCargoText(), {
        fontSize: '16px',
        color: '#ffd84a',
        fontStyle: 'bold',
      })
      .setScrollFactor(0)
      .setDepth(18);
  }

  private refreshGemsText(): void {
    if (this.gemsText === null) return;
    this.gemsText.setText(this.formatCargoText());
    // PSV-1902 UX — a full hold silently stops the crystal magnet (by
    // design); without a signal players read it as "pickup is broken".
    // Red counter + explicit FULL marker while at cap.
    const full = this.myGems >= classById(this.myShipClassId).cargoCapacity;
    this.gemsText.setColor(full ? '#ff5f5f' : '#ffd84a');
  }

  /** PSV-1728 — gems ARE the cargo; HUD shows hold usage vs class cap. */
  private formatCargoText(): string {
    const cap = classById(this.myShipClassId).cargoCapacity;
    const full = this.myGems >= cap;
    return `Cargo: ${this.myGems}/${cap} ◆${full ? '  FULL — spend gems to mine' : ''}`;
  }

  private createBuffsHud(): void {
    if (this.buffsContainer !== null) return;
    this.buffsContainer = this.scene.add
      .container(10, 60)
      .setScrollFactor(0)
      .setDepth(18);
  }

  private addOrReplaceBuff(bonusType: string, expiresAt: number): void {
    if (this.buffsContainer === null) return;
    const existing = this.activeBuffs.get(bonusType);
    if (existing !== undefined) {
      existing.expiresAt = expiresAt;
      return;
    }
    const text = this.scene.add.text(0, 0, '', {
      fontSize: '14px',
      color: '#cceeff',
      fontStyle: 'bold',
    });
    this.buffsContainer.add(text);
    this.activeBuffs.set(bonusType, { expiresAt, text });
    this.restackBuffsHud();
  }

  private restackBuffsHud(): void {
    let i = 0;
    for (const entry of this.activeBuffs.values()) {
      entry.text.setPosition(0, i * 18);
      i += 1;
    }
  }

  private updateBuffsHud(): void {
    if (this.activeBuffs.size === 0) return;
    const now = Date.now();
    let pruned = false;
    for (const [bonusType, entry] of this.activeBuffs) {
      const remaining = Math.max(0, entry.expiresAt - now);
      if (remaining === 0) {
        entry.text.destroy();
        this.activeBuffs.delete(bonusType);
        pruned = true;
        continue;
      }
      const seconds = Math.ceil(remaining / 1000);
      entry.text.setText(`Active: ${bonusType.toUpperCase()} (${seconds}s)`);
    }
    if (pruned) this.restackBuffsHud();
  }

  private handleSessionRecovering(): void {
    // eslint-disable-next-line no-console
    console.info('[arena] session → recovering — clearing local ships and buffer');
    for (const ship of this.ships.values()) {
      ship.sprite.destroy();
      ship.label.destroy();
      this.effects?.detachShipHpBar(ship.userId);
    }
    this.ships.clear();
    this.hpMaxByUserId.clear();
    this.gameStateBuffer.length = 0;
    this.isJoined = false;
    // Stage 5: drop prediction state too — server may respawn us at a
    // new position on the other side of recovery, so the old myPredicted
    // is meaningless. Fresh PlayerJoined will reinitialize.
    this.myPredicted = null;
    this.inputHistory.length = 0;
    this.clientPhysicsAccumulator = 0;
    this.showLoading('Reconnecting…');
    this.armLoadingFallback();
  }

  /**
   * PSV-1338 Bug 2 — session went `Recovering → Active`. Server may have
   * TTL-evicted us during the disconnect window; either way the input
   * loop will resume sending ShipInput and the server (re)creates our
   * ship on receipt. The actual sprite/prediction re-init happens on
   * the next `GameState` carrying our userId via the safety net in
   * `handleGameState` — no special path needed here.
   *
   * What this handler DOES contribute:
   *   - Log the transition for diagnostics.
   *   - Re-arm the loading fallback with a fresh 5s budget against the
   *     post-reconnect timing (without this, a reconnect that landed
   *     mid-fallback would leave the user with a tighter window than
   *     intended).
   */
  private handleSessionActive(): void {
    // eslint-disable-next-line no-console
    console.info('[arena] session → active');
    if (this.myPredicted === null || !this.ships.has(this.myUserId)) {
      // Still pre-recovery sprite — keep the loading overlay alive but
      // re-arm the budget so the next GameState within 5s gets a fair
      // shot at hiding it via the standard path.
      this.armLoadingFallback();
    }
  }

  // ---------- prediction + reconciliation (Stage 5) ----------

  private physicsTickPredicted(): void {
    if (this.myPredicted === null || this.matchConfig === null) return;
    applyPhysicsStep(this.myPredicted, this.currentInput, CLIENT_PHYSICS_DT, this.matchConfig, this.myEngineMax, this.myAgility);
  }

  private reconcileSelf(snapshot: GameStatePayload): void {
    if (this.myPredicted === null || this.matchConfig === null) return;
    const meServer = snapshot.ships.find((s) => s.userId === this.myUserId);
    if (meServer === undefined) return;

    // 1. Drop acked inputs. Stage 5+ servers carry `lastInputSeq` per ship;
    //    fall back to "ack nothing" against pre-Stage-5 servers so the
    //    history grows naturally until INPUT_HISTORY_MAX trims it.
    const lastAckedSeq =
      typeof meServer.lastInputSeq === 'number' ? meServer.lastInputSeq : -1;
    while (this.inputHistory.length > 0 && this.inputHistory[0]!.seq <= lastAckedSeq) {
      this.inputHistory.shift();
    }

    // 2. Rebuild authoritative baseline: server's state + replay of every
    //    unacked input FOR ITS ACTUAL DURATION (consecutive `t` deltas,
    //    clamped to 2× the send interval). The old single-1/60s-step
    //    replay underestimated each input by ~5× at the 20Hz cadence and
    //    ~12× after the 10Hz change — baseline lagged prediction by
    //    30-40px under thrust, tripping a HARD snap on every snapshot
    //    (PSV-1726: «корабль дёргается, а не едет»). Substepping at the
    //    physics dt keeps the integration identical to both sims.
    const baseline: PredictedShipState = {
      x: meServer.x,
      y: meServer.y,
      vx: meServer.vx,
      vy: meServer.vy,
      angle: meServer.angle,
      angularVel: meServer.angularVel,
      hp: meServer.hp,
      lastServerSnapshot: {
        x: meServer.x,
        y: meServer.y,
        vx: meServer.vx,
        vy: meServer.vy,
        angle: meServer.angle,
        angularVel: meServer.angularVel,
        tick: snapshot.tick,
        t: snapshot.t,
      },
    };
    const maxInputSpanS = (INPUT_INTERVAL_MS * 2) / 1000;
    for (let i = 0; i < this.inputHistory.length; i++) {
      const input = this.inputHistory[i]!;
      const nextT =
        i + 1 < this.inputHistory.length ? this.inputHistory[i + 1]!.t : Date.now();
      let remaining = Math.max(
        CLIENT_PHYSICS_DT,
        Math.min((nextT - input.t) / 1000, maxInputSpanS),
      );
      while (remaining > 1e-4) {
        const dt = Math.min(remaining, CLIENT_PHYSICS_DT);
        applyPhysicsStep(baseline, input, dt, this.matchConfig, this.myEngineMax, this.myAgility);
        remaining -= dt;
      }
    }

    // 3. Compare predicted vs baseline-corrected prediction.
    const dx = baseline.x - this.myPredicted.x;
    const dy = baseline.y - this.myPredicted.y;
    const distSq = dx * dx + dy * dy;

    if (distSq < 1) {
      // Sub-pixel divergence — no visible adjustment needed.
      this.myPredicted.lastServerSnapshot = baseline.lastServerSnapshot;
      return;
    }

    if (distSq >= RECONCILE_THRESHOLD_PX * RECONCILE_THRESHOLD_PX) {
      // Hard snap. Large divergence (network blip, missed input, physics
      // drift) — one visible jump beats a slow visible drag.
      // eslint-disable-next-line no-console
      console.warn(
        `[arena] hard reconcile self: dist=${Math.sqrt(distSq).toFixed(1)}px`,
      );
      this.myPredicted = baseline;
      return;
    }

    // Soft lerp — blend toward baseline by a fixed fraction. Converges
    // over a handful of snapshots; invisible to the player at 20Hz.
    this.myPredicted.x += dx * RECONCILE_LERP_ALPHA;
    this.myPredicted.y += dy * RECONCILE_LERP_ALPHA;
    this.myPredicted.vx = lerp(this.myPredicted.vx, baseline.vx, RECONCILE_LERP_ALPHA);
    this.myPredicted.vy = lerp(this.myPredicted.vy, baseline.vy, RECONCILE_LERP_ALPHA);
    this.myPredicted.angle = angleLerp(
      this.myPredicted.angle,
      baseline.angle,
      RECONCILE_LERP_ALPHA,
    );
    this.myPredicted.angularVel = lerp(
      this.myPredicted.angularVel,
      baseline.angularVel,
      RECONCILE_LERP_ALPHA,
    );
    this.myPredicted.hp = baseline.hp; // authoritative — never interpolate
    this.myPredicted.lastServerSnapshot = baseline.lastServerSnapshot;
  }

  private renderPredictedSelf(): void {
    if (this.myPredicted === null) return;
    const ship = this.ships.get(this.myUserId);
    if (ship === undefined) return;
    // Overrides any write that `renderInterpolated`'s self-skip might have
    // missed (defence in depth) — sprite reflects prediction every frame.
    this.applyTransform(ship, this.myPredicted.x, this.myPredicted.y, this.myPredicted.angle);
    // PSV-1729 — engine exhaust while thrust is held (own ship only;
    // remote thrust isn't on the wire). One particle per frame reads as
    // a continuous flame at 60fps without hurting the pool.
    if (this.currentInput.thrust > 0) {
      const ownTrail = cosmeticById(this.myCosmetics.trail);
      this.effects?.addEngineFlame(
        this.myPredicted.x,
        this.myPredicted.y,
        this.myPredicted.angle,
        ownTrail?.color,
      );
    }
    // PSV-1888 — remote trails: thrust isn't on the wire, but snapshot
    // velocity is a good-enough proxy. Only trail-EQUIPPED remotes flame
    // (keeps particle pressure bounded and makes the cosmetic pop).
    for (const sh of this.lastSnapshotShips) {
      if (sh.userId === this.myUserId) continue;
      const entry = this.ships.get(sh.userId);
      const trail = cosmeticById(entry?.appliedTrail);
      if (entry === undefined || trail === null) continue;
      if (Math.hypot(sh.vx, sh.vy) < 40) continue;
      this.effects?.addEngineFlame(
        entry.sprite.x,
        entry.sprite.y,
        entry.sprite.rotation,
        trail.color,
      );
    }
  }

  // ---------- render ----------

  private renderInterpolated(now: number): void {
    const buf = this.gameStateBuffer;
    if (buf.length === 0) return;
    if (buf.length === 1) {
      // Only one snapshot — snap everyone to it.
      this.applySnapshot(buf[0]!.payload);
      return;
    }

    const renderTime = now - RENDER_DELAY_MS;

    // Bracket search using local receivedAt. Buffer is ordered by
    // insertion time, so receivedAt is monotonic.
    //
    // Stage 6 v3: also capture neighbors S0 (one older than the
    // bracket's S1) and S3 (one newer than S2) for Catmull-Rom tangent
    // derivation. If either neighbor is missing — buffer warmup, edge
    // of FIFO trim, or skew between RENDER_DELAY and arrival rate —
    // applyInterpolated falls back to linear lerp per-ship.
    //
    // Steady-state geometry with cap 4 and delay 75ms: snapshots arrive
    // every 50ms, so buf[0..3].receivedAt = T-150, T-100, T-50, T.
    // renderTime = T-75 → bracket = (buf[1], buf[2]). S0 = buf[0],
    // S3 = buf[3], alpha = 0.5. Catmull-Rom engages.
    let s0: BufferedSnapshot | null = null;
    let s1: BufferedSnapshot | null = null;
    let s2: BufferedSnapshot | null = null;
    let s3: BufferedSnapshot | null = null;
    for (let i = 0; i < buf.length - 1; i++) {
      const a = buf[i]!;
      const b = buf[i + 1]!;
      if (a.receivedAt <= renderTime && b.receivedAt >= renderTime) {
        s1 = a;
        s2 = b;
        if (i - 1 >= 0) s0 = buf[i - 1]!;
        if (i + 2 < buf.length) s3 = buf[i + 2]!;
        break;
      }
    }

    if (s1 === null || s2 === null) {
      // renderTime falls outside the buffer (we're either ahead of the
      // newest snapshot or behind the oldest). Snap to whichever end
      // is closest — at startup, that's the only snapshot; mid-session
      // it means a fan-out gap larger than RENDER_DELAY_MS.
      const tail = buf[buf.length - 1]!;
      this.applySnapshot(tail.payload);
      return;
    }

    const span = s2.receivedAt - s1.receivedAt;
    const alpha = span > 0 ? (renderTime - s1.receivedAt) / span : 1;
    this.applyInterpolated(
      s0?.payload ?? null,
      s1.payload,
      s2.payload,
      s3?.payload ?? null,
      alpha,
    );
  }

  /**
   * Bug C safety net (Stage 5): create a remote sprite on first sight in
   * a GameState payload. PlayerJoined events are not guaranteed to reach
   * us — they fire once at spawn time and are missed if we joined the
   * arena after the spawn or were mid-reconnect when it fired. Remotes
   * still appear in every GameState the server emits, so a one-shot
   * create-on-demand here is enough; a later PlayerJoined (e.g. on the
   * player's next respawn) will refresh the label via `handlePlayerJoined`.
   * Placeholder identity (`Pilot_<6char>` / `Scout`) is used in the
   * interim — matches the format `handlePlayerJoined` falls back to when
   * fields are missing.
   */
  private ensureRemoteSprite(shipData: GameStateShip): ShipSprite {
    let ship = this.ships.get(shipData.userId);
    if (ship === undefined) {
      const placeholderName = `Pilot_${shipData.userId.slice(0, 6)}`;
      ship = this.createShipSprite(
        shipData.userId,
        placeholderName,
        // PSV-1726 — snapshots carry the class for late joiners; older
        // servers omit it → starter visuals until PlayerJoined refreshes.
        shipData.shipClass ?? STARTER_CLASS_ID,
        shipData.x,
        shipData.y,
        shipData.angle,
        false, // isMine — remote
      );
      this.ships.set(shipData.userId, ship);
      // eslint-disable-next-line no-console
      console.info(
        `[arena] created remote sprite from GameState (PlayerJoined missed): ${placeholderName} (${shipData.userId})`,
      );
    }
    return ship;
  }

  /** Snap every known sprite to a single snapshot — used as fallback. */
  private applySnapshot(snap: GameStatePayload): void {
    for (const s of snap.ships) {
      if (s.userId === this.myUserId) continue; // own ship — see renderPredictedSelf
      const ship = this.ensureRemoteSprite(s);
      this.applyTransform(ship, s.x, s.y, s.angle);
    }
  }

  /**
   * Stage 6 v3: Catmull-Rom interpolation across the (S1, S2) render
   * segment, with S0 and S3 as outer neighbors used to derive tangents.
   *
   * Per-ship fallback to linear lerp when:
   *   - `s0` or `s3` payload is null (buffer warmup, FIFO trim edge), or
   *   - the ship is absent from either neighbor snapshot (just-spawned
   *     or about-to-despawn — Catmull-Rom undefined without 4 anchor
   *     points).
   *
   * Catmull-Rom sidesteps server velocity semantics entirely (no
   * dependence on `vx` / `vy` / `angularVel`), so the Stage 6 v1/v2
   * off-by-one bug — server emits velocity at the END of a tick, not
   * the velocity LEAVING that tick — can't reach this codepath.
   */
  private applyInterpolated(
    s0: GameStatePayload | null,
    s1: GameStatePayload,
    s2: GameStatePayload,
    s3: GameStatePayload | null,
    alpha: number,
  ): void {
    const s0ById = s0 !== null ? new Map<string, GameStateShip>() : null;
    if (s0 !== null && s0ById !== null) {
      for (const sh of s0.ships) s0ById.set(sh.userId, sh);
    }
    const s2ById = new Map<string, GameStateShip>();
    for (const sh of s2.ships) s2ById.set(sh.userId, sh);
    const s3ById = s3 !== null ? new Map<string, GameStateShip>() : null;
    if (s3 !== null && s3ById !== null) {
      for (const sh of s3.ships) s3ById.set(sh.userId, sh);
    }

    for (const s1Ship of s1.ships) {
      if (s1Ship.userId === this.myUserId) continue; // own ship — see renderPredictedSelf
      const ship = this.ensureRemoteSprite(s1Ship);
      const s2Ship = s2ById.get(s1Ship.userId);
      if (s2Ship === undefined) {
        // Ship dropped between snapshots (will fully disappear on next
        // PlayerLeft). Hold last known position.
        this.applyTransform(ship, s1Ship.x, s1Ship.y, s1Ship.angle);
        continue;
      }
      if (s2Ship.isReset === true) {
        // Spawn / respawn discontinuity — snap to next. Neighbor
        // tangents would be garbage across this boundary anyway.
        this.applyTransform(ship, s2Ship.x, s2Ship.y, s2Ship.angle);
        continue;
      }

      const s0Ship = s0ById?.get(s1Ship.userId);
      const s3Ship = s3ById?.get(s1Ship.userId);

      let x: number;
      let y: number;
      let angle: number;
      if (s0Ship !== undefined && s3Ship !== undefined) {
        // Full Catmull-Rom: tangent at endpoint = half of neighbor
        // secant. Hermite formula then connects (s1, s2) with C1
        // continuity to the adjacent segments.
        const mX1 = (s2Ship.x - s0Ship.x) / 2;
        const mX2 = (s3Ship.x - s1Ship.x) / 2;
        const mY1 = (s2Ship.y - s0Ship.y) / 2;
        const mY2 = (s3Ship.y - s1Ship.y) / 2;
        x = hermiteInterp(s1Ship.x, s2Ship.x, mX1, mX2, alpha);
        y = hermiteInterp(s1Ship.y, s2Ship.y, mY1, mY2, alpha);
        angle = catmullRomInterpAngle(
          s0Ship.angle,
          s1Ship.angle,
          s2Ship.angle,
          s3Ship.angle,
          alpha,
        );
      } else {
        // Missing neighbor(s) — degrade to linear lerp for this ship.
        x = lerp(s1Ship.x, s2Ship.x, alpha);
        y = lerp(s1Ship.y, s2Ship.y, alpha);
        angle = angleLerp(s1Ship.angle, s2Ship.angle, alpha);
      }
      this.applyTransform(ship, x, y, angle);
    }

    // Catch ships present in S2 but not S1 (newly joined between the
    // two render-pair broadcasts). No interpolation possible — snap.
    for (const s2Ship of s2.ships) {
      if (s2Ship.userId === this.myUserId) continue; // own ship — see renderPredictedSelf
      const hadS1 = s1.ships.some((p) => p.userId === s2Ship.userId);
      if (hadS1) continue; // already handled in the main loop above
      const ship = this.ensureRemoteSprite(s2Ship);
      this.applyTransform(ship, s2Ship.x, s2Ship.y, s2Ship.angle);
    }
  }

  // ---------- sprite helpers ----------

  private createShipSprite(
    userId: string,
    guestName: string,
    shipClass: string,
    x: number,
    y: number,
    angle: number,
    isMine: boolean,
  ): ShipSprite {
    const color = isMine ? COLOR_LOCAL : COLOR_REMOTE;
    const sprite = this.drawShip(color);
    const labelColor = isMine ? '#eeeeee' : '#cccccc';
    const label = this.scene.add
      .text(0, 0, this.formatLabel(guestName, shipClass), {
        fontSize: '12px',
        color: labelColor,
        align: 'center',
      })
      .setOrigin(0.5, 0);
    const ship: ShipSprite = {
      userId,
      guestName,
      shipClass,
      isMine,
      sprite,
      label,
    };
    // PSV-1726 — per-class tint + scale on top of the base footprint.
    // Legacy/placeholder classes resolve to the starter (no-op visuals).
    this.applyClassVisual(sprite, shipClass);
    this.applyTransform(ship, x, y, angle);
    // PSV-1336 Phase 4 — attach HP bar with cached HpMax (or schema
    // default). Subsequent `ShipDamaged` events will refresh both the
    // cached HpMax (in case it differs from the default) and the bar
    // fill ratio. The bar tracks the sprite's world position via the
    // EffectsManager's per-frame update.
    const hpMax =
      this.hpMaxByUserId.get(userId) ?? ArenaController.DEFAULT_HP_MAX;
    this.hpMaxByUserId.set(userId, hpMax);
    this.effects?.attachShipHpBar(sprite, userId, hpMax);
    return ship;
  }

  private drawShip(color: number): Phaser.GameObjects.Sprite {
    const sprite = this.scene.add.sprite(0, 0, SHIP_TEXTURE_KEY);
    sprite.setOrigin(0.5, 0.5); // rotate around center
    sprite.setScale(this.baseShipScale());
    // Local ship = no tint (full sprite colors). Remote ship gets a
    // slightly muted multiply tint so the player can pick themselves
    // out at a glance, replacing the previous local/remote color split
    // we lost when triangle fills became texture pixels.
    if (color !== COLOR_LOCAL) sprite.setTint(COLOR_REMOTE);
    return sprite;
  }

  /**
   * PSV-1726 — base sprite scale mapping the source PNG width onto the
   * SHIP_LENGTH footprint. Class scales MULTIPLY this (a 1.3× Crusader is
   * 1.3 × the base footprint), so both drawShip and applyClassVisual
   * derive from the same number.
   */
  private baseShipScale(): number {
    const texture = this.scene.textures.get(SHIP_TEXTURE_KEY);
    const source = texture.source[0];
    const sourceWidth =
      source !== undefined && source.width > 0 ? source.width : SHIP_LENGTH;
    return SHIP_LENGTH / sourceWidth;
  }

  /**
   * PSV-1726 — placeholder per-class visuals until dedicated sprites land:
   * multiply-tint + footprint scale from the class def. The starter's
   * neutral 0xffffff tint is SKIPPED (not applied) so it never overwrites
   * the remote-ship mute tint drawShip put on.
   */
  private applyClassVisual(
    sprite: Phaser.GameObjects.Sprite,
    classId: string,
  ): void {
    const def = classById(classId);
    if (def.tint !== 0xffffff) sprite.setTint(def.tint);
    sprite.setScale(def.scale * this.baseShipScale());
  }

  private formatLabel(guestName: string, shipClass: string): string {
    // PSV-1726 — resolve the wire class id ('delta-fighter') to its display
    // name ('Delta-Fighter'); legacy ids fall back to the starter's name.
    return `${guestName}\n[${classById(shipClass).name}]`;
  }

  private applyTransform(
    ship: ShipSprite,
    x: number,
    y: number,
    angle: number,
  ): void {
    ship.sprite.setPosition(x, y);
    // PSV-1336 — see SHIP_ART_ANGLE_OFFSET docblock for the +π/2 rationale.
    ship.sprite.setRotation(angle + SHIP_ART_ANGLE_OFFSET);
    // Label stays axis-aligned (no rotation) and tracks below the ship.
    ship.label.setPosition(x, y + SHIP_LENGTH / 2 + 4);
  }

  // ---------- UI overlays ----------

  private showLoading(text: string): void {
    if (this.loadingText !== null) {
      this.loadingText.setText(text);
      return;
    }
    const cam = this.scene.cameras.main;
    this.loadingText = this.scene.add
      .text(cam.centerX, cam.centerY, text, {
        fontSize: '24px',
        color: '#ffffff',
        backgroundColor: '#000000bb',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0); // ignore camera scroll — anchor to viewport
  }

  private hideLoading(): void {
    if (this.loadingText !== null) {
      this.loadingText.destroy();
      this.loadingText = null;
    }
    if (this.loadingFallbackTimer !== null) {
      this.loadingFallbackTimer.remove(false);
      this.loadingFallbackTimer = null;
    }
  }

  /**
   * Arm the Bug-2 fallback. If `PlayerJoined` never reaches us (lost
   * during a reconnect window, or the WS dropped right after our subscribe
   * landed), this timer hides the loading overlay anyway. The ship will
   * still pop in once GameState arrives — see the safety net in
   * `handleGameState`.
   */
  private armLoadingFallback(): void {
    // Idempotent: callable both at start() and at every reconnect via
    // handleSessionRecovering(). Clear any pending timer first so the
    // previous arm's callback can't fire against the new loading text.
    if (this.loadingFallbackTimer !== null) {
      this.loadingFallbackTimer.remove(false);
      this.loadingFallbackTimer = null;
    }
    this.loadingFallbackTimer = this.scene.time.delayedCall(
      ArenaController.LOADING_FALLBACK_MS,
      () => {
        if (this.loadingText === null) return; // already hidden
        // eslint-disable-next-line no-console
        console.warn(
          `[arena] PlayerJoined not received within ${ArenaController.LOADING_FALLBACK_MS}ms — hiding loading anyway`,
        );
        this.hideLoading();
      },
    );
  }

  private createPilotsOnlineText(): void {
    this.pilotsOnlineText = this.scene.add
      .text(10, 10, 'Pilots online: 0', {
        fontSize: '14px',
        color: '#ffffff',
      })
      .setScrollFactor(0);
  }

  /**
   * PSV-1336 Phase 1 — bottom-left controls hint. Stage 4 removed the
   * original "Arrows / WASD — move" overlay from `ArenaScene`; we
   * reintroduce it here alongside the pilots counter so the player knows
   * Space / mouse triggers fire.
   */
  private createControlsHintText(): void {
    const cam = this.scene.cameras.main;
    this.controlsHintText = this.scene.add
      .text(
        10,
        cam.height - 10,
        // PSV-1911 — the hint matches the actual input surface (device
        // probe, not this.touch — the joystick spawns later in start()).
        isTouchDevice()
          ? 'Drag LEFT half — move    Tap RIGHT half — fire    Hold — info    \ud83d\udcac — chat'
          : 'WASD / Arrows — move    Space / Mouse — fire    B — shop    Q — quests    Enter — chat',
        {
          fontSize: '12px',
          color: '#9aa0b4',
        },
      )
      .setOrigin(0, 1)
      .setScrollFactor(0);
  }

  private updatePilotsOnlineText(): void {
    if (this.pilotsOnlineText === null) return;
    const count = this.ships.size;
    const haveMine = this.ships.has(this.myUserId);
    const suffix = count === 1 && haveMine ? ' (you)' : '';
    this.pilotsOnlineText.setText(`Pilots online: ${count}${suffix}`);
  }
}

// ---------- module-private helpers ----------

/** PSV-1726 — registry key for a projectile visual (volley-safe). */
function projectileKey(shooterId: string, fireTime: number, barrel: number): string {
  return `${shooterId}-${fireTime}-${barrel}`;
}

function stringField(raw: Record<string, unknown>, key: string): string | null {
  const v = raw[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function numberField(raw: Record<string, unknown>, key: string): number | null {
  const v = raw[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function isValidShip(s: unknown): s is GameStateShip {
  if (s === null || typeof s !== 'object') return false;
  const ship = s as Record<string, unknown>;
  return (
    typeof ship.userId === 'string' &&
    typeof ship.x === 'number' &&
    typeof ship.y === 'number' &&
    typeof ship.angle === 'number'
  );
}

/**
 * Shortest-path angular lerp in radians. Returns `from` advanced toward
 * `to` by `alpha` fraction of the shortest signed delta, in [-π, π].
 *
 * NO post-lerp Wrap: server does not wrap its angles, so wrapping the
 * result here would create a perpetual creep-and-flip cycle whenever
 * client and server hold the same heading in different "winding"
 * representations — e.g. server keeps `angle = 4.5 rad` while a single
 * Wrap would push the client into `-1.78 rad`, after which subsequent
 * reconciliations walk the shortest path back to π, hit Wrap, jump to
 * -π, walk again, ... visually identical to a slow continuous spin
 * around the ship's axis (REPORT v12.7 Bug B). Caller is free to wrap
 * the result themselves if they have a reason to bound the range.
 *
 * Previously used `Phaser.Math.Angle.ShortestBetween`, which expects
 * DEGREES — passing radians silently under-corrects by a factor of
 * ~57 and combined with the implicit Wrap produced the spin bug
 * described above.
 */
/**
 * Shortest signed delta from `from` to `to` in radians, wrapped to
 * (-π, π]. Used for both `angleLerp` (Stage 5 Bug B fix) and
 * `catmullRomInterpAngle` (Stage 6 v3) to avoid spinning the long
 * way when server emits angles in different windings.
 */
function shortestDelta(from: number, to: number): number {
  const TAU = Math.PI * 2;
  let delta = (to - from) % TAU;
  if (delta > Math.PI) delta -= TAU;
  else if (delta < -Math.PI) delta += TAU;
  return delta;
}

function angleLerp(from: number, to: number, alpha: number): number {
  return from + shortestDelta(from, to) * alpha;
}

function lerp(a: number, b: number, alpha: number): number {
  return a + (b - a) * alpha;
}

/**
 * Stage 6: cubic Hermite interpolation. Smooth C1 curve from `p0` at
 * `t=0` to `p1` at `t=1` with tangents `m0`, `m1` expressed in
 * full-segment units (i.e. position change per Δt of the segment).
 *
 * Stage 6 v3 derives tangents from neighbor secants (Catmull-Rom) so
 * the caller doesn't depend on server velocity semantics — the prior
 * `velocity * dtSec` formulation suffered an off-by-one because server
 * emits the velocity that arrived AT a position, not the one leaving
 * it. See REPORT v12.8 §1 for the analysis.
 *
 * Basis functions are the standard Hermite forms:
 *   h00 =  2t³ - 3t² + 1   ←  weight for p0
 *   h10 =     t³ - 2t² + t ←  weight for m0
 *   h01 = -2t³ + 3t²       ←  weight for p1
 *   h11 =     t³ -    t²   ←  weight for m1
 */
function hermiteInterp(
  p0: number,
  p1: number,
  m0: number,
  m1: number,
  t: number,
): number {
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * p0 + h10 * m0 + h01 * p1 + h11 * m1;
}

/**
 * Stage 6 v3: angle-domain Catmull-Rom interpolation.
 *
 * Render segment is (a1, a2); a0 and a3 are the previous / next
 * snapshot neighbors. Tangents are derived from neighbor secants
 * (half of `a2 - a0` and `a3 - a1` respectively) — this avoids the
 * velocity-semantics off-by-one that broke Stage 6 v1/v2 and matches
 * standard uniform Catmull-Rom.
 *
 * Each delta is wrapped to shortest signed path so the curve never
 * spins the long way through ±π. The endpoint `a2` is normalized
 * relative to `a1` so Hermite operates on a strictly-monotonic angle
 * range (same trick as the old `hermiteInterpAngle`).
 */
function catmullRomInterpAngle(
  a0: number,
  a1: number,
  a2: number,
  a3: number,
  t: number,
): number {
  const deltaMain = shortestDelta(a1, a2);
  const a2Norm = a1 + deltaMain;
  // Tangents in radians-per-segment. Each neighbor delta is
  // independently shortest-pathed so server's unwrapped windings
  // don't bleed into the curve.
  const m1 = shortestDelta(a0, a2Norm) / 2;
  const m2 = shortestDelta(a1, a3) / 2;
  return hermiteInterp(a1, a2Norm, m1, m2, t);
}

/**
 * Mirror of `server/src/physics.ts:physicsStep` with two intentional
 * differences:
 *   - Input is passed as an argument (server reads `ship.lastInput`).
 *   - The 150ms INPUT_STALE_MS zero-input fallback is omitted — client
 *     always uses what it just sampled. Visible mismatch only during
 *     long network blips, where reconciliation's hard-snap covers us.
 *   - The NaN sanity reset is omitted — server-driven; if the client
 *     gets a non-finite state it will reconcile against the next server
 *     snapshot regardless.
 *
 * If you patch one side's formula, patch the other — Stage 5 spec
 * explicitly declines to share a package, on the assumption that
 * unilateral patches are rare.
 */
function applyPhysicsStep(
  state: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    angle: number;
    angularVel: number;
  },
  input: { thrust: -1 | 0 | 1; turn: -1 | 0 | 1; aim?: number },
  dt: number,
  config: MatchConfig,
  // PSV-1785: the SERVER caps speed at the per-ship engineMax (200 for
  // the starter), NOT MatchConfig.ShipSpeed (320). Predicting against 320
  // drew a 320/ω turn arc that the authoritative 200/ω sim kept snapping
  // back — felt like a huge turn radius + unhittable near targets. The
  // caller passes its class-tracked engineMax so both sims cap alike.
  speedCap: number,
  // PSV-1726: per-class rotation speed (rad/s) replaces the global
  // MatchConfig.ShipRotationSpeed, same both-sims rationale as speedCap.
  // Non-positive values fall back to the config constant (defensive).
  agility: number,
): void {
  // 1. Turn. PSV-1726 mouse-steer: a finite `aim` is a desired heading —
  //    rotate toward it capped by agility and STOP on alignment. MUST stay
  //    formula-identical to server physics.ts (both-sims contract).
  const turnRate = agility > 0 ? agility : config.ShipRotationSpeed;
  if (typeof input.aim === 'number' && Number.isFinite(input.aim)) {
    let delta = input.aim - state.angle;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    const maxStep = turnRate * dt;
    const step = Math.max(-maxStep, Math.min(maxStep, delta));
    state.angle += step;
    state.angularVel = dt > 0 ? step / dt : 0;
  } else if (input.turn !== 0) {
    state.angularVel = input.turn * turnRate;
    state.angle += state.angularVel * dt;
  } else {
    state.angularVel *= ANGULAR_DECAY_PER_TICK;
    if (Math.abs(state.angularVel) < ANGULAR_DEADBAND) state.angularVel = 0;
    state.angle += state.angularVel * dt;
  }

  // 2. Thrust — accelerate along the heading vector (cos, sin).
  if (input.thrust !== 0) {
    const accel = input.thrust * config.ShipAcceleration;
    state.vx += Math.cos(state.angle) * accel * dt;
    state.vy += Math.sin(state.angle) * accel * dt;
  }

  // 3. Drag — `ShipDrag` is the multiplier per 1/60s, framerate-scaled
  //    via `pow(drag, dt*60)`. Matches server.
  const dragFactor = Math.pow(config.ShipDrag, dt * 60);
  state.vx *= dragFactor;
  state.vy *= dragFactor;

  // 4. Speed cap — preserve direction. See the `speedCap` param note:
  //    tier-based engineMax, matching the server's cap source.
  const speed = Math.hypot(state.vx, state.vy);
  if (speed > speedCap) {
    const scale = speedCap / speed;
    state.vx *= scale;
    state.vy *= scale;
  }

  // 5. Integrate position.
  state.x += state.vx * dt;
  state.y += state.vy * dt;

  // 6. Arena boundary clamp — circular wall, zero outward radial vel only.
  const distSq = state.x * state.x + state.y * state.y;
  const r = config.ArenaRadius;
  if (distSq > r * r) {
    const dist = Math.sqrt(distSq);
    const nx = state.x / dist;
    const ny = state.y / dist;
    state.x = nx * r;
    state.y = ny * r;
    const radialVel = state.vx * nx + state.vy * ny;
    if (radialVel > 0) {
      state.vx -= radialVel * nx;
      state.vy -= radialVel * ny;
    }
  }
}
